/**
 * Hebrew UI strings, keyed by the English source text passed to tr().
 * Placeholders like {n} must appear unchanged in the translation. Wording avoids gendered imperatives
 * ("יש ל…", noun forms) so it addresses every user the same way.
 */
export const he: Record<string, string> = {
  // ───────── common ─────────
  'Page not found': 'הדף לא נמצא',
  'never': 'אף פעם',
  'just now': 'הרגע',
  'This PC': 'מחשב זה',
  'Loading': 'טוען',
  'Not allowed': 'אין הרשאה',
  'Something went wrong': 'משהו השתבש',
  'Reference:': 'מזהה לבירור:',
  'Try again': 'לנסות שוב',
  'Copied': 'הועתק',
  'Copy': 'העתקה',
  'of {total}': 'מתוך {total}',
  'Previous': 'הקודם',
  'Next': 'הבא',
  "Confirm it's you": 'אימות זהות',
  'Cancel': 'ביטול',
  'Confirm': 'אישור',
  'Save': 'שמירה',
  'Edit': 'עריכה',
  'Create': 'יצירה',
  'Add': 'הוספה',
  'Delete': 'מחיקה',
  'Remove': 'הסרה',
  'Close': 'סגירה',
  'Back': 'חזרה',
  'Continue': 'המשך',
  'Apply': 'החלה',
  'Choose': 'בחירה',
  'Choose…': 'בחירה…',
  'View': 'צפייה',
  'Download': 'הורדה',
  'Retry': 'ניסיון חוזר',
  'Revoke': 'ביטול',
  'Pause': 'השהיה',
  'Done': 'בוצע',
  'None': 'אין',
  'none': 'ללא',
  'yes': 'כן',
  'unknown': 'לא ידוע',
  'All': 'הכול',
  'Filter': 'סינון',
  'This action is sensitive. Re-enter your password.': 'זוהי פעולה רגישה. יש להזין שוב את הסיסמה.',
  'This action is sensitive. Re-enter your password and a code from your authenticator app.': 'זוהי פעולה רגישה. יש להזין שוב את הסיסמה וקוד מאפליקציית האימות.',
  'Password': 'סיסמה',
  'Authentication code': 'קוד אימות',
  'Email': 'אימייל',
  'Name': 'שם',
  'Description': 'תיאור',
  'Status': 'סטטוס',
  'State': 'מצב',
  'Details': 'פרטים',
  'Reason': 'סיבה',
  'Reason (optional)': 'סיבה (אופציונלי)',
  'Comma separated': 'מופרדות בפסיקים',
  'Time zone': 'אזור זמן',
  'Created': 'נוצר',
  'Started': 'התחיל',
  'Finished': 'הסתיים',
  'Updated': 'עודכן',
  'When': 'מתי',
  'Size': 'גודל',
  'Files': 'קבצים',
  'files': 'קבצים',
  'folders': 'תיקיות',
  '{n} files': '{n} קבצים',
  'Problems': 'בעיות',
  'Selection': 'בחירה',
  'Destination': 'יעד',
  'Progress': 'התקדמות',
  'Security': 'אבטחה',
  'Overview': 'סקירה',
  'ID': 'מזהה',
  'Device ID': 'מזהה מחשב',

  // ───────── lifecycle states (StateBadge) ─────────
  'Completed': 'הושלם',
  'Committed': 'נשמר',
  'Active': 'פעיל',
  'Ready': 'מוכן',
  'Success': 'הצליח',
  'Protected': 'מוגן',
  'Online': 'מחובר',
  'Offline': 'לא מחובר',
  'Partial': 'חלקי',
  'Retrying': 'מנסה שוב',
  'Committing': 'שומר',
  'Running': 'פועל',
  'Preparing': 'בהכנה',
  'Queued': 'בתור',
  'Pending': 'ממתין',
  'Upload verified': 'ההעלאה אומתה',
  'Sample verified': 'אומת בדגימה',
  'Restore verified': 'אומת בשחזור',
  'Failed': 'נכשל',
  'Expired': 'פג תוקף',
  'Canceled': 'בוטל',
  'Quarantined': 'בהסגר',
  'Retired': 'הוצא משימוש',
  'Suspended': 'מושעה',
  'Dead': 'כשל סופי',
  'Unprotected': 'לא מוגן',
  'Pending deletion': 'ממתין למחיקה',
  'Trialing': 'תקופת ניסיון',
  'Past due': 'באיחור תשלום',
  'Unpaid': 'לא שולם',
  'Paused': 'מושהה',
  'Revoked': 'נשלל',
  'Draft': 'טיוטה',
  'Skipped': 'דולג',
  'Delivered': 'נמסר',
  'Verified': 'אומת',
  'Unverified': 'לא אומת',
  'Not verified': 'לא אומת',
  'Open': 'פתוח',
  'Resolved': 'נפתר',
  'Acknowledged': 'התקבל',
  'Provisioning': 'בהקמה',
  'Paid': 'שולם',
  'Void': 'מבוטל',
  'Uncollectible': 'לא ניתן לגבייה',
  'Superseded': 'הוחלף',
  'Rotating': 'בהחלפה',
  'Scanning': 'סורק',
  'Uploading': 'מעלה',
  'Finalizing': 'מסיים',
  'Enrolling': 'ברישום',
  'Queued|toast': 'נוסף לתור',

  // ───────── enum values shown verbatim ─────────
  'critical': 'קריטי',
  'warning': 'אזהרה',
  'info': 'מידע',
  'warn': 'אזהרה',
  'error': 'שגיאה',
  'debug': 'ניפוי',
  'denied': 'נדחה',
  'failure': 'כשל',
  'scheduled': 'מתוזמן',
  'schedule': 'מתוזמן',
  'manual': 'ידני',
  'catchup': 'השלמה',
  'retry': 'ניסיון חוזר',
  'drill': 'תרגול',
  'device': 'מחשב',
  'group': 'קבוצה',
  'default': 'ברירת מחדל',
  'tenant': 'ארגון',
  'rename': 'שינוי שם',
  'skip': 'דילוג',
  'overwrite': 'דריסה',
  'admin': 'מנהל',
  'viewer': 'צפייה בלבד',
  'daily': 'יומית',
  'weekly': 'שבועית',
  'monthly': 'חודשית',
  'yearly': 'שנתית',
  'intraday': 'תוך-יומית',
  'last': 'אחרונה',
  'hold': 'החזקה',

  // ───────── shell & navigation ─────────
  'Account': 'חשבון',
  'Operator console': 'מסוף מפעילים',
  'Sign out': 'התנתקות',
  'Dark mode': 'מצב כהה',
  'Light mode': 'מצב בהיר',
  'Operator support session': 'סשן תמיכה של מפעיל',
  'content access enabled': 'גישה לתוכן מופעלת',
  'no file-content access': 'ללא גישה לתוכן קבצים',
  'reason: “{reason}”': 'סיבה: „{reason}”',
  'ends {time}': 'מסתיים {time}',
  'End session': 'סיום הסשן',
  '{name} (support)': '{name} (תמיכה)',
  'Backup': 'גיבוי',
  'Organization': 'ארגון',
  'Choose organization…': 'בחירת ארגון…',
  'Main': 'ראשי',
  'Computers': 'מחשבים',
  'Alerts': 'התראות',
  'Settings': 'הגדרות',
  'This organization is {status}. Existing backups remain browsable and restorable; new backups and enrollments may be paused.': 'הארגון במצב {status}. הגיבויים הקיימים נשארים זמינים לעיון ולשחזור; גיבויים ורישומים חדשים עשויים להיות מושהים.',
  'Verify your email address to add computers.': 'יש לאמת את כתובת האימייל כדי להוסיף מחשבים.',
  'Resend the email': 'שליחת האימייל מחדש',
  'Operators only': 'למפעילים בלבד',
  'This console is for platform operators.': 'המסוף הזה מיועד למפעילי הפלטפורמה.',
  'Operator': 'מפעיל',
  'Tenants': 'ארגונים',
  'Plans & limits': 'תוכניות ומגבלות',
  'Account recovery': 'שחזור חשבונות',
  'Jobs & queues': 'משימות ותורים',
  'Storage health': 'תקינות אחסון',
  'Maintenance': 'תחזוקה',
  'Agent releases': 'גרסאות סוכן',
  'Audit': 'ביקורת',
  'Operators must enable two-factor authentication. Go to Account → Security.': 'מפעילים חייבים להפעיל אימות דו-שלבי. יש לעבור אל חשבון ← אבטחה.',

  // ───────── dashboard ─────────
  'Getting started': 'צעדים ראשונים',
  'Last 24 hours': '24 השעות האחרונות',
  'Stored': 'מאוחסן',
  'Needs attention': 'דורש טיפול',
  'No computers yet': 'אין עדיין מחשבים',
  'All alerts': 'כל ההתראות',

  // ───────── sign-in & account creation ─────────
  'Every file, accounted for.': 'כל קובץ, שמור ומתועד.',
  'Sign in': 'התחברות',
  'Welcome back.': 'שמחים לראות אותך שוב.',
  'Forgot password?': 'שכחת את הסיסמה?',
  'Create an account': 'יצירת חשבון',
  'Two-factor verification': 'אימות דו-שלבי',
  'Enter one of your recovery codes.': 'יש להזין אחד מקודי השחזור.',
  'Enter the 6-digit code from your authenticator app.': 'יש להזין את הקוד בן 6 הספרות מאפליקציית האימות.',
  'Recovery code': 'קוד שחזור',
  'Code': 'קוד',
  'Verify': 'אימות',
  'Use an authenticator code': 'שימוש בקוד מאפליקציית אימות',
  'Use a recovery code instead': 'שימוש בקוד שחזור במקום',
  'Start protecting your business': 'מתחילים להגן על העסק',
  '14-day trial. No card required.': 'ניסיון ל-14 יום. ללא כרטיס אשראי.',
  'Your name': 'שם מלא',
  'Work email': 'אימייל בעבודה',
  'At least 12 characters. A passphrase works well.': 'לפחות 12 תווים. משפט סיסמה הוא בחירה טובה.',
  'Create account': 'יצירת חשבון',
  'Continue with Google': 'המשך עם Google',
  'or': 'או',
  'One last step': 'עוד צעד אחד',
  'Start again': 'להתחיל מחדש',
  'Signed in with Google as {email}.': 'מחובר עם Google בתור {email}.',
  'Your business name, as your team knows it.': 'שם העסק, כפי שהצוות מכיר אותו.',
  'Signing in with Google took too long. Please try again.': 'ההתחברות עם Google נמשכה זמן רב מדי. אפשר לנסות שוב.',
  'Signing in with Google was canceled.': 'ההתחברות עם Google בוטלה.',
  'We could not sign you in with Google. Please try again.': 'לא הצלחנו להתחבר עם Google. אפשר לנסות שוב.',
  'Google has not confirmed this email address, so it cannot be used to sign in.': 'Google לא אישרה את כתובת המייל הזו, ולכן אי אפשר להתחבר איתה.',
  'This account is turned off. Ask your organization’s admin for help.': 'החשבון הזה כבוי. אפשר לפנות למנהל הארגון לעזרה.',
  'This email address is already connected to a different Google account. Sign in with your password instead.': 'כתובת המייל הזו כבר מחוברת לחשבון Google אחר. אפשר להתחבר עם הסיסמה במקום.',
  'This action is sensitive. Sign in with Google again to confirm, then try the action once more.': 'זוהי פעולה רגישה. לאישור יש להתחבר שוב עם Google, ואז לנסות את הפעולה פעם נוספת.',
  'Connected to Google': 'מחובר ל-Google',
  'You can sign in with your Google account.': 'אפשר להתחבר עם חשבון Google.',
  'You sign in with Google, so you have no ZeroGap password. Add one to also sign in with your email address.': 'ההתחברות שלך היא עם Google, ולכן אין סיסמה ל-ZeroGap. אפשר להוסיף סיסמה כדי להתחבר גם עם כתובת המייל.',
  'Password added': 'הסיסמה נוספה',
  'This sign-up has expired. Continue with Google again.': 'תוקף ההרשמה פג. אפשר להמשיך עם Google שוב.',
  'Confirm it is you by signing in with Google again': 'לאישור הזהות יש להתחבר שוב עם Google',
  'I have read and agree, on behalf of the organization, to the': 'קראתי ואישרתי, בשם הארגון, את',
  'Terms of Service and Privacy Policy': 'תנאי השימוש ומדיניות הפרטיות',
  'Contents': 'תוכן העניינים',
  'Already have an account?': 'כבר יש לך חשבון?',
  'Email verification': 'אימות אימייל',
  'Verifying…': 'מאמת…',
  'Your email is verified.': 'כתובת האימייל אומתה.',
  'Continue to ZeroGap': 'המשך ל-ZeroGap',
  'Reset your password': 'איפוס סיסמה',
  'If an account exists for {email}, a reset link is on its way. It expires in one hour.': 'אם קיים חשבון עבור {email}, נשלח אליו קישור לאיפוס. תוקפו יפוג בעוד שעה.',
  'Send reset link': 'שליחת קישור לאיפוס',
  'Back to sign in': 'חזרה להתחברות',
  'Choose a new password': 'בחירת סיסמה חדשה',
  'Password updated.': 'הסיסמה עודכנה.',
  'All other sessions were signed out.': 'כל שאר ההתחברויות נותקו.',
  'New password': 'סיסמה חדשה',
  'At least 12 characters. Two-factor authentication stays enabled.': 'לפחות 12 תווים. האימות הדו-שלבי יישאר מופעל.',
  'Update password': 'עדכון סיסמה',
  'Join {org}': 'הצטרפות אל {org}',
  'organization': 'הארגון',
  'Invitation for': 'הזמנה עבור',
  'as an admin.': 'בתפקיד מנהל.',
  'as a view-only member.': 'בהרשאת צפייה בלבד.',
  'You already have an account.': 'כבר יש לך חשבון.',
  'Sign in to accept': 'יש להתחבר כדי לאשר',
  "You're signed in as {email}, but this invitation is for a different address.": 'התחברת בתור {email}, אבל ההזמנה נשלחה לכתובת אחרת.',
  'Sign out to accept': 'יש להתנתק כדי לאשר',
  'At least 12 characters.': 'לפחות 12 תווים.',
  'Accept invitation': 'אישור ההזמנה',

  // ───────── computers ─────────
  'Search computers': 'חיפוש מחשבים',
  'No computers match': 'אין מחשבים תואמים',
  'Select all': 'בחירת הכול',
  'Computer': 'מחשב',
  'Select {name}': 'בחירת {name}',
  'Signed': 'חתום',
  'Expires {time}.': 'בתוקף עד {time}.',
  'Back up now': 'גיבוי עכשיו',
  'Key': 'מפתח',
  'Issued': 'הונפק',
  'No backups yet': 'אין עדיין גיבויים',
  'No events': 'אין אירועים',

  // ───────── policies ─────────
  'between {from} and {to}': 'בין {from} ל-{to}',
  'Schedule': 'תזמון',
  'Default': 'ברירת מחדל',
  'Essential workstation': 'תחנת עבודה בסיסית',
  'Business files': 'קבצים עסקיים',
  'Long-term archive': 'ארכיון לטווח ארוך',
  'Choose at least one profile folder (e.g. Documents).': 'יש לבחור לפחות תיקיית פרופיל אחת (למשל מסמכים).',
  'This policy needs at least one folder to back up. Add the business folders to protect.': 'המדיניות צריכה לפחות תיקייה אחת לגיבוי. יש להוסיף את התיקיות העסקיות להגנה.',
  'Retention keeps nothing.': 'הגדרות השמירה אינן שומרות דבר.',
  'Documents': 'מסמכים',
  'Desktop': 'שולחן העבודה',
  'Pictures': 'תמונות',
  'Downloads': 'הורדות',
  'Music': 'מוזיקה',
  'Videos': 'סרטונים',
  'Make default': 'הגדרה כברירת מחדל',
  'What to back up': 'מה לגבות',
  'Only these users': 'רק המשתמשים האלה',
  'Daily': 'יומי',
  'Weekly': 'שבועי',
  'Monthly': 'חודשי',
  'Yearly': 'שנתי',
  'Every few hours': 'כל כמה שעות',
  'Until': 'עד',
  'Required': 'חובה',
  'Off': 'כבוי',
  'On': 'פעיל',

  // ───────── restores ─────────
  'Location': 'מיקום',
  'changed or deleted later': 'שונה או נמחק מאוחר יותר',
  'No matches': 'אין תוצאות',
  'Review': 'סיכום',
  'Restore': 'שחזור',
  'View-only access': 'הרשאת צפייה בלבד',
  'offline': 'לא מחובר',
  'Where should the files go?': 'לאן לשחזר את הקבצים?',
  'Skip': 'דילוג',
  'Overwrite': 'דריסה',
  'Reason (required for operators)': 'סיבה (חובה למפעילים)',
  'From': 'מקור',
  '{files} files · {size}': '{files} קבצים · {size}',
  'To': 'יעד',
  'No restores yet': 'אין עדיין שחזורים',
  'By': 'על ידי',
  'operator': 'מפעיל',
  'Data': 'נתונים',
  'No problems': 'אין בעיות',

  // ───────── organization ─────────
  'Admin': 'מנהל',
  'User': 'משתמש',
  'Role': 'תפקיד',
  'Joined': 'הצטרפות',
  'Invitation sent': 'ההזמנה נשלחה',
  'Send invitation': 'שליחת הזמנה',
  'Users': 'משתמשים',
  'Backups': 'גיבויים',
  'Who': 'מי',
  'Action': 'פעולה',
  'Target': 'יעד',
  'Computer {name}': 'מחשב {name}',
  'System': 'מערכת',
  'Newer': 'חדשים יותר',
  'Older': 'ישנים יותר',
  'Failed backups': 'גיבויים שנכשלו',
  'Signed JSON events for tools such as n8n. Each request carries': 'אירועי JSON חתומים לכלים כמו n8n. כל בקשה נושאת את הכותרת',
  'Payloads never contain credentials or file contents.': 'התוכן לעולם אינו כולל פרטי גישה או תוכן קבצים.',
  'Add endpoint': 'הוספת נקודת קצה',
  'Events': 'אירועים',
  '{n} failures': '{n} כשלים',
  'Deliveries': 'משלוחים',
  'Send test': 'שליחת בדיקה',
  'Add webhook endpoint': 'הוספת נקודת קצה ל-Webhook',
  'HTTPS URL': 'כתובת HTTPS',
  'Private, loopback, and cloud-metadata addresses are refused.': 'כתובות פרטיות, loopback וכתובות metadata של ענן נדחות.',
  'Plan': 'תוכנית',
  'Limits': 'מגבלות',
  'Plans': 'תוכניות',
  'a month, before VAT': 'לחודש, לפני מע״מ',
  'Invoices': 'חשבוניות',
  'No invoices yet': 'אין עדיין חשבוניות',
  // Names of the built-in plans (plans.name), translated where the portal shows them.
  'Essentials': 'בסיסי',
  'Office': 'משרד',
  'Practice': 'מורחב',
  'Disabled': 'כבוי',

  // ───────── admin: plans & assistant usage ─────────
  'Assistant usage (this month)': 'שימוש בעוזר (החודש)',
  '{used} / {allowed} turns': '{used} מתוך {allowed} פניות',
  'No assistant turns this month': 'אין פניות לעוזר החודש',
  'Turns': 'פניות',
  'New plan': 'תוכנית חדשה',
  'New plan…': 'תוכנית חדשה…',
  'Edit plan: {name}': 'עריכת תוכנית: {name}',
  'Lowercase letters, digits, hyphens. Cannot be changed later.': 'אותיות לטיניות קטנות, ספרות ומקפים. לא ניתן לשנות מאוחר יותר.',
  'Fixed once created.': 'קבוע לאחר היצירה.',
  'Price a month, before VAT ({currency})': 'מחיר לחודש, לפני מע״מ ({currency})',
  'Trial days': 'ימי ניסיון',
  'Computer limit': 'מגבלת מחשבים',
  'Seat limit': 'מגבלת משתמשים',
  'Storage (GB)': 'אחסון (GB)',
  'Phone limit': 'מגבלת טלפונים',
  'Phones are counted apart from computers. 0 means no phones.': 'טלפונים נספרים בנפרד ממחשבים. 0 פירושו שאין טלפונים.',
  'Longest time to keep deleted phone files': 'הזמן הארוך ביותר לשמירת קבצים שנמחקו בטלפון',
  'Each organization picks up to this.': 'כל ארגון בוחר עד המשך הזה.',
  'Assistant turns / month': 'פניות לעוזר לחודש',
  '0 disables the assistant even if the organization turns it on.': '0 משבית את העוזר גם אם הארגון מפעיל אותו.',
  'Stripe price ID (optional)': 'מזהה מחיר ב-Stripe (רשות)',
  'Active (selectable for new subscriptions)': 'פעילה (ניתנת לבחירה למנויים חדשים)',
  'This changes the limit for every tenant already subscribed to this plan, not just new ones. Assign a tenant to a plan from its Subscription card.':
    'השינוי חל על כל ארגון שכבר נמצא בתוכנית הזו, לא רק על חדשים. כדי לשייך ארגון לתוכנית יש להשתמש בכרטיס המנוי שלו.',
  'Manual and Stripe subscriptions both draw their price and limits from these plans.': 'מנויים ידניים ומנויי Stripe שואבים את המחיר והמגבלות שלהם מהתוכניות האלה.',
  'Price': 'מחיר',
  'Seats': 'משתמשים',
  'Phones': 'טלפונים',
  'Trial': 'ניסיון',
  'Assistant turns': 'פניות לעוזר',
  'Stripe price': 'מחיר ב-Stripe',
  '/mo': 'לחודש',
  'Edit…': 'עריכה…',
  'No plans yet': 'אין עדיין תוכניות',
  'General': 'כללי',
  'Organization name': 'שם הארגון',
  'Saved': 'נשמר',
  'Provider': 'ספק',
  'Bucket': 'דלי (Bucket)',
  'Deletion canceled': 'המחיקה בוטלה',

  // ───────── account ─────────
  'Enabled': 'מופעל',
  'New recovery codes': 'קודי שחזור חדשים',
  'Disable': 'השבתה',
  'Or enter this key manually': 'או להזין את המפתח הזה ידנית',
  'Enable': 'הפעלה',
  'Required for operators.': 'חובה למפעילים.',
  'Set up': 'הגדרה',
  'Save these recovery codes now — each works once and they are not shown again.': 'יש לשמור את קודי השחזור עכשיו — כל קוד עובד פעם אחת והם לא יוצגו שוב.',
  'Current password': 'הסיסמה הנוכחית',
  'Password changed': 'הסיסמה שונתה',
  'Change password': 'שינוי סיסמה',
  'signed in {signedIn} · active {active}': 'התחברות {signedIn} · פעילות {active}',

  // ───────── operator console ─────────
  'Reason (recorded in the audit log, visible to the customer)': 'סיבה (נרשמת ביומן הביקורת וגלויה ללקוח)',
  'At least 10 characters': 'לפחות 10 תווים',
  'Platform overview': 'סקירת הפלטפורמה',
  'Active computers': 'מחשבים פעילים',
  'Dead tasks': 'משימות בכשל סופי',
  'Inspect queues': 'בדיקת התורים',
  'Task queues': 'תורי משימות',
  'Kind': 'סוג',
  'Count': 'כמות',
  'Oldest': 'הוותיקה ביותר',
  'Queues are empty': 'התורים ריקים',
  'Storage provisioning & jobs (24 h)': 'הקמת אחסון ומשימות (24 שעות)',
  'Search name or ID…': 'חיפוש לפי שם או מזהה…',
  'Search tenants': 'חיפוש ארגונים',
  'Tenant': 'ארגון',
  'Storage': 'אחסון',
  'Open support session…': 'פתיחת סשן תמיכה…',
  'Suspend…': 'השעיה…',
  'Reactivate…': 'הפעלה מחדש…',
  'Purge after': 'מחיקה סופית אחרי',
  'Re-run provisioning': 'הרצת ההקמה מחדש',
  'Lock': 'נעילה',
  '{days} d': '{days} ימים',
  'Last error': 'שגיאה אחרונה',
  'Subscription': 'מנוי',
  'Record manual billing…': 'רישום חיוב ידני…',
  'Period end': 'סוף התקופה',
  'Grace until': 'תקופת חסד עד',
  'Members': 'חברים',
  'Invite admin…': 'הזמנת מנהל…',
  'No members': 'אין חברים',
  "After a catalog rebuild, invite the customer's admin to restore access.": 'אחרי בנייה מחדש של הקטלוג, יש להזמין את מנהל הלקוח כדי להחזיר גישה.',
  '2FA': 'אימות דו-שלבי',
  'no 2FA': 'ללא אימות דו-שלבי',
  'seen {time}': 'נראה {time}',
  'backup {time}': 'גיבוי {time}',
  'Maintenance runs': 'ריצות תחזוקה',
  'Schedule tenant deletion…': 'תזמון מחיקת הארגון…',
  'Open a support session': 'פתיחת סשן תמיכה',
  'Start session': 'התחלת סשן',
  "You will act inside this customer's organization for up to 60 minutes. The session is shown to you with a banner and recorded in the customer's activity log with your identity. You never impersonate a customer user.": 'הפעולה תתבצע בתוך הארגון של הלקוח למשך עד 60 דקות. הסשן מסומן בבאנר ונרשם ביומן הפעילות של הלקוח עם זהות המפעיל. אין בשום שלב התחזות למשתמש של הלקוח.',
  'Access': 'גישה',
  'Support (no file contents)': 'תמיכה (ללא תוכן קבצים)',
  'Include restores & downloads': 'כולל שחזורים והורדות',
  'Content access lets you restore and download customer files. It requires a fresh re-authentication and is flagged in the audit log.': 'גישה לתוכן מאפשרת לשחזר ולהוריד קבצי לקוח. היא מחייבת אימות מחדש ומסומנת ביומן הביקורת.',
  'Suspend tenant': 'השעיית ארגון',
  'Reactivate tenant': 'הפעלת ארגון מחדש',
  'New backups and enrollments stop. Restores and downloads remain available. No data is deleted.': 'גיבויים ורישומים חדשים נעצרים. שחזורים והורדות נשארים זמינים. שום מידע אינו נמחק.',
  'Invite an admin (account recovery)': 'הזמנת מנהל (שחזור חשבון)',
  'Invitation emailed to {email}': 'ההזמנה נשלחה אל {email}',
  "For organizations with no working admin (for example after a catalog rebuild). The person receives a single-use link and sets their own password; you never see or set it. Recorded in the customer's activity log.": 'לארגונים ללא מנהל פעיל (למשל אחרי בנייה מחדש של הקטלוג). הנמען מקבל קישור חד-פעמי וקובע סיסמה בעצמו; המפעיל לעולם אינו רואה או קובע אותה. הפעולה נרשמת ביומן הפעילות של הלקוח.',
  'Re-run storage provisioning': 'הרצה מחדש של הקמת האחסון',
  'Idempotent: re-checks bucket, versioning, lock, lifecycle, maintenance identity, and bucket policy.': 'אידמפוטנטי: בודק מחדש דלי, ניהול גרסאות, נעילה, מחזור חיים, זהות תחזוקה ומדיניות הדלי.',
  'Schedule tenant deletion': 'תזמון מחיקת ארגון',
  'Purge starts after 30 days and waits for Object Lock retention and holds.': 'המחיקה הסופית מתחילה אחרי 30 יום וממתינה לתום השמירה של Object Lock ולשחרור ההחזקות.',
  'Record manual billing': 'רישום חיוב ידני',
  'Invoice number (optional)': 'מספר חשבונית (אופציונלי)',
  'Invoice total (₪)': 'סכום החשבונית (₪)',
  'Payment actually received': 'התשלום התקבל בפועל',
  'Reset two-factor authentication': 'איפוס אימות דו-שלבי',
  'Send password reset link': 'שליחת קישור לאיפוס סיסמה',
  'Sign out everywhere': 'ניתוק מכל המכשירים',
  'Clear sign-in lockout': 'ביטול נעילת התחברות',
  'Recover access without ever seeing or setting a password. Every action requires a reason, is audited with your identity and the affected user, and the user is notified by email.': 'שחזור גישה בלי לראות או לקבוע סיסמה. כל פעולה דורשת סיבה, נרשמת ביומן עם זהות המפעיל והמשתמש המושפע, והמשתמש מקבל הודעה באימייל.',
  'Search by email (min. 2 characters)…': 'חיפוש לפי אימייל (לפחות 2 תווים)…',
  'Search users': 'חיפוש משתמשים',
  'Search for a user': 'חיפוש משתמש',
  '{name} · joined {time}': '{name} · הצטרפות {time}',
  '{n} organization(s)': 'ארגונים: {n}',
  'Done and audited': 'בוצע ונרשם ביומן',
  'Durable tasks live in Postgres with leases; abandoned leases are re-queued automatically. Dead tasks exhausted their retries.': 'משימות עמידות נשמרות ב-Postgres עם חכירות; חכירות נטושות חוזרות לתור אוטומטית. משימות בכשל סופי מיצו את הניסיונות החוזרים.',
  'Tenant ID': 'מזהה ארגון',
  'Attempts': 'ניסיונות',
  'system': 'מערכת',
  'One bucket per tenant; one principal and prefix per computer.': 'דלי אחד לכל ארגון; זהות וקידומת אחת לכל מחשב.',
  'Provider capabilities: {provider}': 'יכולות הספק: {provider}',
  'object lock enforced': 'Object Lock נאכף',
  'object lock not enforced': 'Object Lock לא נאכף',
  'Retention, garbage collection, verification, restore drills, usage, key re-wrapping, and disaster-recovery catalog rebuild.': 'שמירה, איסוף אשפה, אימות, תרגולי שחזור, שימוש, עטיפה מחדש של מפתחות ובנייה מחדש של הקטלוג להתאוששות מאסון.',
  'Run task…': 'הרצת משימה…',
  'Stats': 'נתונים',
  'Run maintenance task': 'הרצת משימת תחזוקה',
  'Task': 'משימה',
  'Recovery point ID': 'מזהה נקודת שחזור',
  'Releases are signed offline with the release key; this server only verifies and relays them. Rollout cohorts are chosen deterministically per computer.': 'הגרסאות נחתמות במצב לא מקוון במפתח השחרור; השרת רק מאמת ומעביר אותן. קבוצות ההפצה נקבעות באופן דטרמיניסטי לכל מחשב.',
  'No releases registered': 'לא נרשמו גרסאות',
  'Register with scripts/release/publish.ts after signing.': 'הרישום נעשה עם scripts/release/publish.ts אחרי החתימה.',
  'Version': 'גרסה',
  'Platform': 'פלטפורמה',
  'Rollout': 'הפצה',
  'Computers on version': 'מחשבים בגרסה',
  'Rollout percent': 'אחוז הפצה',
  'Start rollout': 'התחלת הפצה',
  'Hash-chained, append-only for application roles. Export a copy to independent storage for stronger tamper resistance.': 'שרשרת גיבוב שאפשר רק להוסיף לה מתפקידי היישום. ייצוא עותק לאחסון עצמאי מחזק את העמידות בפני שינויים.',
  'Verify chain': 'אימות השרשרת',
  'Export NDJSON': 'ייצוא NDJSON',
  'Chain intact': 'השרשרת תקינה',
  'Chain broken at event {id}': 'השרשרת שבורה באירוע {id}',
  '{n} events checked.': 'נבדקו {n} אירועים.',
  'Filter by tenant ID': 'סינון לפי מזהה ארגון',
  'Tenant filter': 'סינון ארגון',
  'platform': 'פלטפורמה',
  'op': 'מפעיל',

  // ───────── product tour ─────────
  'Take the tour': 'סיור מודרך',
  'Step {n} of {total}': 'שלב {n} מתוך {total}',
  'Skip tour': 'דילוג על הסיור',
  'Finish': 'סיום',
  'Replay any time': 'אפשר לחזור בכל עת',
  'Open this tour again from here whenever you need a refresher.': 'מכאן אפשר לפתוח את הסיור שוב בכל פעם שצריך תזכורת.',

  // ───────── navigation (plain-language portal) ─────────
  'Menu': 'תפריט',
  'Backup plans': 'תוכניות גיבוי',
  'Get files back': 'שחזור קבצים',
  'Team': 'צוות',
  'Activity': 'פעילות',
  'Subscription & usage': 'מנוי ושימוש',

  // ───────── plain status & errors ─────────
  'Online now': 'מחובר עכשיו',
  'Last seen {time}': 'נראה לאחרונה {time}',
  'Not connected yet': 'עדיין לא התחבר',
  'Turn the computer on and connect it to the internet. It backs up automatically.': 'יש להדליק את המחשב ולחבר אותו לאינטרנט. הגיבוי יתבצע אוטומטית.',
  'Removed': 'הוסר',
  'Its backups are kept and can still be restored.': 'הגיבויים שלו נשמרים ועדיין אפשר לשחזר מהם.',
  'Blocked': 'חסום',
  'Blocked because it was reported lost or stolen. Its backups can still be restored to another computer.': 'נחסם כי דווח שאבד או נגנב. עדיין אפשר לשחזר את הגיבויים שלו למחשב אחר.',
  'Setting up…': 'בהכנה…',
  'The computer is connected and getting ready. This usually takes a few minutes.': 'המחשב מחובר ומתכונן. זה בדרך כלל לוקח כמה דקות.',
  'Backing up now…': 'מגבה עכשיו…',
  'Backed up {time}': 'גובה {time}',
  "Backups are paused in this computer's backup plan. Resume them on the plan's page.": 'הגיבויים מושהים בתוכנית הגיבוי של המחשב. אפשר לחדש אותם בדף התוכנית.',
  'No backup plan': 'אין תוכנית גיבוי',
  'Choose a backup plan for this computer.': 'יש לבחור תוכנית גיבוי למחשב הזה.',
  'Waiting for its first backup': 'ממתין לגיבוי הראשון',
  'The first backup can take a while. Leave the computer on.': 'הגיבוי הראשון יכול לקחת זמן. כדאי להשאיר את המחשב דולק.',
  'No backup for {time}': 'אין גיבוי כבר {time}',
  "The computer is on, but backups aren't finishing. Try “Back up now” or look at its backup history.": 'המחשב דולק, אבל הגיבויים לא מסתיימים. אפשר לנסות "גיבוי עכשיו" או לבדוק את היסטוריית הגיבויים שלו.',
  'Backup overdue': 'הגיבוי באיחור',
  'a minute': 'דקה',
  '{n} minutes': '{n} דקות',
  'an hour': 'שעה',
  '2 hours': 'שעתיים',
  '{n} hours': '{n} שעות',
  '1 day': 'יום אחד',
  '2 days': 'יומיים',
  '{n} days': '{n} ימים',
  '1 week': 'שבוע',
  '1 month': 'חודש אחד',
  '{n} months': '{n} חודשים',
  '1 year': 'שנה אחת',
  '{n} years': '{n} שנים',
  'This backup plan is not finished yet: choose at least one folder to back up.': 'תוכנית הגיבוי עוד לא הושלמה: יש לבחור לפחות תיקייה אחת לגיבוי.',
  'Some computers still use this plan. Move them to another plan first.': 'יש מחשבים שעדיין פועלים לפי התוכנית. קודם יש להעביר אותם לתוכנית אחרת.',
  'Please confirm your email address first. We sent you a link.': 'קודם יש לאשר את כתובת האימייל. שלחנו קישור.',
  'This computer has no backup plan yet. Choose one first.': 'למחשב הזה עדיין אין תוכנית גיבוי. קודם יש לבחור תוכנית.',
  "Your organization can't make changes right now. Check your subscription.": 'הארגון לא יכול לבצע שינויים כרגע. כדאי לבדוק את המנוי.',
  'Your subscription has no free user places left.': 'במנוי לא נשארו מקומות פנויים למשתמשים.',
  'Your subscription has no free computer places left.': 'במנוי לא נשארו מקומות פנויים למחשבים.',
  'Please confirm that existing files may be replaced.': 'יש לאשר שמותר להחליף קבצים קיימים.',
  'An organization always needs at least one admin.': 'בארגון חייב להיות תמיד לפחות מנהל אחד.',
  'A backup is already running on this computer.': 'כבר רץ גיבוי במחשב הזה.',
  'This computer cannot back up right now: it is still being set up, blocked, or removed.': 'המחשב הזה לא יכול לגבות כרגע: הוא עדיין בהגדרה, חסום או הוסר.',
  'This is not ready yet. Please try again in a moment.': 'זה עוד לא מוכן. אפשר לנסות שוב בעוד רגע.',
  'Some details are not valid. Please check them and try again.': 'חלק מהפרטים לא תקינים. יש לבדוק אותם ולנסות שוב.',
  'Something went wrong on our side. Please try again.': 'משהו השתבש אצלנו. אפשר לנסות שוב.',
  'Too many attempts. Please wait a moment and try again.': 'יותר מדי ניסיונות. כדאי להמתין רגע ולנסות שוב.',
  'Your session has expired. Refresh the page and try again.': 'פג תוקף ההתחברות. יש לרענן את הדף ולנסות שוב.',
  'An account with this email already exists. Sign in instead.': 'כבר קיים חשבון עם האימייל הזה. אפשר להתחבר במקום.',
  "You don't have permission to do this.": 'אין הרשאה לפעולה הזו.',
  'What does this mean?': 'מה זה אומר?',
  'Technical details': 'פרטים טכניים',

  // ───────── backup plans: summaries ─────────
  '{folders} of every user': '{folders} של כל המשתמשים',
  '{folders} of {n} chosen users': '{folders} של {n} משתמשים שנבחרו',
  '{base}, plus one more folder': '{base}, ועוד תיקייה אחת',
  '{base}, plus {n} more folders': '{base}, ועוד {n} תיקיות',
  'No folders chosen yet': 'עדיין לא נבחרו תיקיות',
  'The folder {path}': 'התיקייה {path}',
  '{n} folders, including {path}': '{n} תיקיות, ביניהן {path}',
  'Several times a day (every 3 hours, 08:00–21:00)': 'כמה פעמים ביום (כל 3 שעות, ⁦08:00–21:00⁩)',
  'Every day at {time}': 'כל יום ב-{time}',
  'Every {day} at {time}': 'כל {day} ב-{time}',
  'only on {days}': 'רק בימים {days}',
  'Every hour': 'כל שעה',
  'Every {n} hours': 'כל {n} שעות',
  'Every {days} at {times}': 'בכל {days} ב-{times}',
  'Every day at {times}': 'כל יום ב-{times}',
  'Keeps about {span} of history': 'שומרת היסטוריה של כ-{span}',
  'at any backup in the last {n} days': 'מכל גיבוי ב-{n} הימים האחרונים',
  'from any day in the last {n} days': 'מכל יום ב-{n} הימים האחרונים',
  'from any week in the last {n} weeks': 'מכל שבוע ב-{n} השבועות האחרונים',
  'from any month in the last {n} months': 'מכל חודש ב-{n} החודשים האחרונים',
  'from any year in the last {n} years': 'מכל שנה ב-{n} השנים האחרונות',
  'Only the latest {n} backups are kept.': 'נשמרים רק {n} הגיבויים האחרונים.',
  'You can get files back as they were {parts}.': 'אפשר לשחזר קבצים כפי שהיו {parts}.',
  'Personal files': 'קבצים אישיים',
  'today {time}': 'היום {time}',
  'tomorrow {time}': 'מחר {time}',
  'Used by 1 computer': 'בשימוש במחשב אחד',
  'Used by {n} computers': 'בשימוש ב-{n} מחשבים',

  // ───────── backup plans: what ─────────
  "Each person's files": 'הקבצים של כל משתמש',
  'For every person who signs in to the computer.': 'לכל מי שמתחבר למחשב.',
  'Only for the users chosen under Advanced settings.': 'רק למשתמשים שנבחרו בהגדרות המתקדמות.',
  'Other folders': 'תיקיות נוספות',
  'For example, a shared company folder on a second drive or on a server. Type its full location, like D:\\Shared\\Accounts.': 'למשל תיקייה משותפת של החברה בכונן נוסף או בשרת. יש להקליד את המיקום המלא שלה, כמו ⁦D:\\Shared\\Accounts⁩.',
  'Folder location': 'מיקום התיקייה',
  'Start with a drive letter (like D:\\) or a network address (like \\\\server\\share). On Linux, start with /.': 'המיקום צריך להתחיל באות כונן (כמו ⁦D:\\⁩) או בכתובת רשת (כמו ⁦\\\\server\\share⁩). ב-Linux הוא מתחיל ב-⁦/⁩.',
  'Remove this folder': 'הסרת התיקייה',
  'Add a folder': 'הוספת תיקייה',
  'Network drives such as S: are not visible to the backup app. Use the network address instead, like \\\\server\\share.': 'כונני רשת כמו S: לא גלויים לתוכנת הגיבוי. במקומם יש להשתמש בכתובת הרשת, כמו ⁦\\\\server\\share⁩.',
  'Choose at least one folder to back up.': 'יש לבחור לפחות תיקייה אחת לגיבוי.',
  'Skip some files (optional)': 'דילוג על חלק מהקבצים (לא חובה)',
  'Skip videos and music': 'דילוג על סרטונים ומוזיקה',
  'Skip temporary files': 'דילוג על קבצים זמניים',
  'Skip files bigger than': 'דילוג על קבצים גדולים מ-',
  'Size in GB': 'גודל ב-GB',
  'Check on a computer': 'בדיקה על מחשב',
  'See how much this would back up on one of your computers. Nothing is backed up yet, and nothing is changed.': 'כך אפשר לראות כמה יגובה באחד המחשבים. שום דבר עדיין לא מגובה ושום דבר לא משתנה.',
  'Once a computer is connected, you can check here what this would back up on it.': 'אחרי שמחשב יחובר, אפשר יהיה לבדוק כאן מה יגובה בו.',
  'Computer to check': 'מחשב לבדיקה',
  'Asking the computer…': 'שואלים את המחשב…',
  'Check': 'בדיקה',
  'This usually takes up to a minute.': 'בדרך כלל זה לוקח עד דקה.',
  'The computer did not answer. Is it switched on and connected to the internet?': 'המחשב לא ענה. האם הוא דולק ומחובר לאינטרנט?',
  'The computer could not check these folders.': 'המחשב לא הצליח לבדוק את התיקיות האלה.',
  'On {name} this is {files} files ({size}).': 'ב-{name} מדובר ב-{files} קבצים ({size}).',
  'The computer stopped counting after 45 seconds, so the real amount is larger.': 'המחשב הפסיק לספור אחרי 45 שניות, כך שהכמות האמיתית גדולה יותר.',
  'Not found on {name}:': 'לא נמצאו ב-{name}:',
  'Those folders are skipped on this computer. Check the spelling, or ignore this if they exist only on other computers.': 'במחשב הזה מדלגים על התיקיות האלה. כדאי לבדוק את האיות, או להתעלם אם הן קיימות רק במחשבים אחרים.',
  'Show examples': 'הצגת דוגמאות',
  'Included': 'נכללים',

  // ───────── backup plans: when ─────────
  'Time': 'שעה',
  'Day': 'יום',
  'At': 'בשעה',
  'at': 'בשעה',
  'Several times a day': 'כמה פעמים ביום',
  'Every 3 hours from 08:00 to 21:00. Recommended: little work is ever lost.': 'כל 3 שעות בין 08:00 ל-21:00. מומלץ: כמעט שום עבודה לא הולכת לאיבוד.',
  'Once a day': 'פעם ביום',
  'Choose a time when the computers are usually on.': 'כדאי לבחור שעה שבה המחשבים בדרך כלל דולקים.',
  'Once a week': 'פעם בשבוע',
  'Custom': 'מותאם אישית',
  'Choose exact times and days.': 'בחירת שעות וימים מדויקים.',
  'Warn me if a computer goes without a backup for': 'להתריע אם מחשב לא גובה במשך',
  'Computers that are switched off simply catch up later. You get an alert only when a computer has gone this long without a backup.': 'מחשבים כבויים פשוט משלימים את הגיבוי אחר כך. התראה נשלחת רק כשמחשב לא גובה במשך הזמן הזה.',
  'recommended': 'מומלץ',
  'Next backups (your time): {runs}': 'הגיבויים הבאים (לפי השעה שלך): {runs}',
  'At set times': 'בשעות קבועות',
  'On certain days': 'בימים מסוימים',
  'Every': 'כל',
  'Hours between backups': 'שעות בין גיבויים',
  'hours, from': 'שעות, משעה',
  'until': 'עד',
  'Remove this time': 'הסרת השעה',
  'Add a time': 'הוספת שעה',
  'On these days': 'בימים האלה',
  'Only on these days (leave all unticked for every day)': 'רק בימים האלה (בלי סימון — כל יום)',

  // ───────── backup plans: how long ─────────
  '3 months': '3 חודשים',
  'Recommended for most businesses.': 'מומלץ לרוב העסקים.',
  '7 years': '7 שנים',
  'For business records that must be kept for years.': 'לרשומות עסקיות שחייבים לשמור שנים.',
  'Keep every backup for (days)': 'שמירת כל גיבוי במשך (ימים)',
  'Then one per day for (days)': 'ואז גיבוי אחד ליום במשך (ימים)',
  'One per week for (weeks)': 'גיבוי אחד לשבוע במשך (שבועות)',
  'One per month for (months)': 'גיבוי אחד לחודש במשך (חודשים)',
  'One per year for (years)': 'גיבוי אחד לשנה במשך (שנים)',
  'Always keep at least the last (backups)': 'תמיד לשמור לפחות את הגיבויים האחרונים (מספר)',
  'Longer history uses more storage space. Days when a computer was off do not count.': 'היסטוריה ארוכה יותר תופסת יותר נפח אחסון. ימים שבהם מחשב היה כבוי לא נספרים.',

  // ───────── backup plans: advanced ─────────
  'Advanced settings': 'הגדרות מתקדמות',
  'Most businesses never need to change these.': 'רוב העסקים אף פעם לא צריכים לשנות את אלה.',
  'Clock used for the schedule': 'השעון שלפיו נקבע התזמון',
  "Each computer's own clock (recommended)": 'השעון של כל מחשב (מומלץ)',
  'Spread start times over (minutes)': 'פיזור זמני ההתחלה על פני (דקות)',
  "So that all computers don't start at the same moment.": 'כדי שכל המחשבים לא יתחילו באותו רגע.',
  'Upload speed limit (Mbit/s, 0 = no limit)': 'הגבלת מהירות העלאה (Mbit/s, 0 = ללא הגבלה)',
  'Stop a backup that runs longer than (hours)': 'עצירת גיבוי שנמשך יותר מ- (שעות)',
  'If a backup was missed because the computer was off, run it as soon as the computer is back on': 'אם גיבוי הוחמץ כי המחשב היה כבוי, להריץ אותו ברגע שהמחשב נדלק שוב',
  'Wait while a laptop runs on battery': 'להמתין כשמחשב נייד פועל על סוללה',
  'Wait while on a mobile or limited internet connection': 'להמתין בחיבור אינטרנט סלולרי או מוגבל',
  'Run backups quietly in the background (lower priority)': 'הרצת גיבויים בשקט ברקע (עדיפות נמוכה)',
  'Back up files that are open in other programs (Windows)': 'גיבוי קבצים שפתוחים בתוכנות אחרות (Windows)',
  'When possible (recommended)': 'כשאפשר (מומלץ)',
  'Always': 'תמיד',
  'Never': 'אף פעם',
  'Check a sample of stored backups every (days, 0 = never)': 'בדיקת מדגם מהגיבויים השמורים כל (ימים, 0 = אף פעם)',
  'Do a test restore automatically every (days, 0 = never)': 'שחזור ניסיון אוטומטי כל (ימים, 0 = אף פעם)',
  'Whose personal files': 'הקבצים האישיים של מי',
  'Everyone on the computer': 'כל המשתמשים במחשב',
  'Windows or Linux user names, one per line': 'שמות משתמשים ב-Windows או ב-Linux, אחד בכל שורה',
  'Leave empty to back up everyone.': 'השארה ריקה = גיבוי של כולם.',
  'File rules for IT staff': 'כללי קבצים לאנשי IT',
  'Folders to leave out, one per line': 'תיקיות שלא יגובו, אחת בכל שורה',
  'Name patterns to leave out, one per line': 'תבניות שמות שלא יגובו, אחת בכל שורה',
  'For example *.tmp or node_modules. A * matches any characters.': 'למשל *.tmp או node_modules. הסימן * מתאים לכל רצף תווים.',
  'Only these file types (optional)': 'רק סוגי הקבצים האלה (לא חובה)',
  'Comma separated, for example: docx, xlsx, pdf': 'מופרדים בפסיקים, למשל: docx, xlsx, pdf',
  'Leave out these file types': 'סוגי קבצים שלא יגובו',
  'Comma separated, for example: iso, vhdx': 'מופרדים בפסיקים, למשל: iso, vhdx',

  // ───────── backup plans: pages ─────────
  'A backup plan decides what gets backed up, when, and how long backups are kept. Every computer follows one plan.': 'תוכנית גיבוי קובעת מה מגובה, מתי, וכמה זמן הגיבויים נשמרים. כל מחשב פועל לפי תוכנית אחת.',
  'Create a backup plan': 'יצירת תוכנית גיבוי',
  'No backup plans yet': 'עדיין אין תוכניות גיבוי',
  'Create a plan to choose what gets backed up.': 'יש ליצור תוכנית כדי לבחור מה יגובה.',
  'Not finished': 'לא הושלמה',
  'New computers get this plan': 'מחשבים חדשים מקבלים את התוכנית הזו',
  'The default plan is the one new computers get when they connect. You can move any computer to another plan at any time.': 'תוכנית ברירת המחדל היא התוכנית שמחשבים חדשים מקבלים כשהם מתחברים. בכל עת אפשר להעביר כל מחשב לתוכנית אחרת.',
  'Choose the plan new computers should get': 'יש לבחור את התוכנית שמחשבים חדשים יקבלו',
  'Make it the default': 'קביעה כברירת מחדל',
  'How long to keep': 'כמה זמן לשמור',
  'Name & computers': 'שם ומחשבים',
  'Backup plan created': 'תוכנית הגיבוי נוצרה',
  'Four short questions. The recommended answers are already chosen.': 'ארבע שאלות קצרות. התשובות המומלצות כבר מסומנות.',
  'What should be backed up?': 'מה לגבות?',
  'When should backups run?': 'מתי לגבות?',
  'How far back should you be able to go?': 'כמה אחורה אפשר יהיה לחזור?',
  'Name the plan': 'שם לתוכנית',
  'Which computers should use this plan?': 'אילו מחשבים יפעלו לפי התוכנית?',
  'Use this plan for new computers': 'שימוש בתוכנית הזו למחשבים חדשים',
  'New computers get this plan when they connect. Computers you already have keep their plans.': 'מחשבים חדשים יקבלו את התוכנית הזו כשיתחברו. מחשבים קיימים נשארים בתוכניות שלהם.',
  'Create plan': 'יצירת התוכנית',
  'All your computers already use this plan.': 'כל המחשבים כבר פועלים לפי התוכנית הזו.',
  "You haven't connected any computers yet. You can put them on this plan later.": 'עדיין לא חוברו מחשבים. אפשר יהיה לצרף אותם לתוכנית בהמשך.',
  'now on “{plan}”': 'כרגע בתוכנית "{plan}"',
  'no plan yet': 'עדיין בלי תוכנית',
  '{n} of the chosen computers now keep a longer history. With this plan, their backups older than {span} will be removed.': 'ל-{n} מהמחשבים שנבחרו יש כרגע היסטוריה ארוכה יותר. עם התוכנית הזו, גיבויים שלהם שישנים מ-{span} יימחקו.',
  'Changes saved. They apply from each computer’s next backup.': 'השינויים נשמרו. הם חלים מהגיבוי הבא של כל מחשב.',
  'Backups resumed': 'הגיבויים חודשו',
  'Resume backups': 'חידוש הגיבויים',
  'Pause backups': 'השהיית הגיבויים',
  'Copy of {name}': 'עותק של {name}',
  'Duplicate': 'שכפול',
  'New computers get this plan. Does it back up the right things?': 'מחשבים חדשים מקבלים את התוכנית הזו. האם היא מגבה את הדברים הנכונים?',
  'Check the choices below. Change anything you like, then save, or confirm that it looks good.': 'כדאי לעבור על הבחירות למטה. אפשר לשנות כל דבר ולשמור, או לאשר שהכול נראה טוב.',
  'Great, thanks!': 'מעולה, תודה!',
  'Looks good': 'נראה טוב',
  'Backups are paused for the computers on this plan': 'הגיבויים מושהים במחשבים שבתוכנית הזו',
  'Existing backups are kept, but no new backups run until you resume.': 'הגיבויים הקיימים נשמרים, אבל גיבויים חדשים לא ירוצו עד לחידוש.',
  'This plan is not finished': 'התוכנית הזו לא הושלמה',
  'Choose at least one folder to back up. Computers can only use the plan after that.': 'יש לבחור לפחות תיקייה אחת לגיבוי. רק אחרי זה מחשבים יוכלו להשתמש בתוכנית.',
  'Name of the plan': 'שם התוכנית',
  'What gets backed up': 'מה מגובה',
  'How long backups are kept': 'כמה זמן הגיבויים נשמרים',
  'Computers on this plan': 'מחשבים בתוכנית הזו',
  'Each computer follows exactly one plan. Moving a computer to another plan takes effect at its next backup.': 'כל מחשב פועל לפי תוכנית אחת בדיוק. העברת מחשב לתוכנית אחרת חלה מהגיבוי הבא שלו.',
  'Add computers': 'הוספת מחשבים',
  'No computers use this plan yet': 'עדיין אין מחשבים בתוכנית הזו',
  'Move to another plan…': 'העברה לתוכנית אחרת…',
  'Change history': 'היסטוריית שינויים',
  'Created by {who} · {time}': 'נוצרה על ידי {who} · {time}',
  'Changed by {who} · {time}': 'שונתה על ידי {who} · {time}',
  'ZeroGap': 'ZeroGap',
  'The default plan can’t be deleted. To delete it, first make another plan the default.': 'אי אפשר למחוק את תוכנית ברירת המחדל. כדי למחוק אותה, קודם יש לקבוע תוכנית אחרת כברירת מחדל.',
  'Delete this plan…': 'מחיקת התוכנית…',
  'Unsaved changes': 'שינויים שלא נשמרו',
  'You have unsaved changes': 'יש שינויים שלא נשמרו',
  'Discard': 'ביטול השינויים',
  'Save changes': 'שמירת השינויים',
  'Keep less history?': 'לשמור פחות היסטוריה?',
  'Save and remove older backups': 'שמירה ומחיקת גיבויים ישנים',
  'This plan will keep about {span} of history instead of {before}.': 'התוכנית תשמור היסטוריה של כ-{span} במקום {before}.',
  'Backups older than that will be removed within a few hours, on all {n} computers on this plan. This can’t be undone.': 'גיבויים ישנים יותר יימחקו תוך כמה שעות, בכל {n} המחשבים שבתוכנית. אי אפשר לבטל את זה.',
  'Make “{name}” the default plan?': 'לקבוע את "{name}" כתוכנית ברירת המחדל?',
  'New computers will now get this plan': 'מעכשיו מחשבים חדשים יקבלו את התוכנית הזו',
  'New computers will get this plan when they connect.': 'מחשבים חדשים יקבלו את התוכנית הזו כשיתחברו.',
  'Computers you already have keep their current plans. To move them, use “Add computers”.': 'מחשבים קיימים נשארים בתוכניות הנוכחיות שלהם. כדי להעביר אותם, יש להשתמש ב"הוספת מחשבים".',
  'Pause backups?': 'להשהות את הגיבויים?',
  'Backups paused': 'הגיבויים מושהים',
  'The {n} computers on this plan will stop backing up until you resume. Their existing backups are kept.': '{n} המחשבים בתוכנית הזו יפסיקו לגבות עד לחידוש. הגיבויים הקיימים שלהם נשמרים.',
  'Plan deleted': 'התוכנית נמחקה',
  'Computers moved to this plan': 'המחשבים הועברו לתוכנית',
  'Add computers to this plan': 'הוספת מחשבים לתוכנית',
  'Move to this plan': 'העברה לתוכנית הזו',
  'Delete “{name}”?': 'למחוק את "{name}"?',
  'Move computers and delete': 'העברת המחשבים ומחיקה',
  'Delete plan': 'מחיקת התוכנית',
  'No computers use this plan. Deleting it does not affect any backups.': 'אין מחשבים בתוכנית הזו. המחיקה לא משפיעה על אף גיבוי.',
  '{n} computers use this plan, and there is no other finished plan to move them to. Create another plan first.': '{n} מחשבים פועלים לפי התוכנית, ואין תוכנית מוכנה אחרת להעביר אותם אליה. קודם יש ליצור תוכנית נוספת.',
  '{n} computers use this plan. Choose the plan they should move to:': '{n} מחשבים פועלים לפי התוכנית. לאיזו תוכנית להעביר אותם?',
  'Their existing backups are kept. From now on they follow the plan you choose.': 'הגיבויים הקיימים שלהם נשמרים. מעכשיו הם יפעלו לפי התוכנית שנבחרה.',
  'Change backup plan': 'שינוי תוכנית גיבוי',
  'Change backup plan for {n} computers': 'שינוי תוכנית הגיבוי של {n} מחשבים',
  'Backup plan changed. It applies from the next backup.': 'תוכנית הגיבוי שונתה. השינוי חל מהגיבוי הבא.',
  'Use this plan': 'שימוש בתוכנית הזו',
  'current': 'נוכחית',
  'This plan keeps a shorter history. Backups older than {span} will be removed.': 'התוכנית הזו שומרת היסטוריה קצרה יותר. גיבויים ישנים מ-{span} יימחקו.',
  'Need something different?': 'צריך משהו אחר?',

  // ───────── alerts in plain words ─────────
  'a computer': 'מחשב',
  'Urgent': 'דחוף',
  'For your information': 'לידיעה',
  'Open the computer': 'פתיחת המחשב',
  "A backup didn't finish on {name}": 'גיבוי לא הסתיים ב-{name}',
  'This often fixes itself at the next backup. If it keeps happening, open the computer to see why.': 'לרוב זה מסתדר בגיבוי הבא. אם זה חוזר, כדאי לפתוח את המחשב ולראות למה.',
  '{name} has not been backed up for {time}': '{name} לא גובה כבר {time}',
  '{name} has not been backed up yet': '{name} עדיין לא גובה',
  '{name} has not connected for {time}': '{name} לא התחבר כבר {time}',
  '{name} has not connected for a long time': '{name} לא התחבר כבר הרבה זמן',
  'If it was replaced or is no longer used, remove it. If it was lost or stolen, block it. Its backups are kept either way.': 'אם הוא הוחלף או לא בשימוש, אפשר להסיר אותו. אם הוא אבד או נגנב, כדאי לחסום אותו. בכל מקרה הגיבויים שלו נשמרים.',
  'Your storage is {pct}% full': 'האחסון מלא ב-{pct}%',
  'Backups continue. To make room, upgrade your subscription or keep less history in your backup plans.': 'הגיבויים ממשיכים. כדי לפנות מקום, אפשר לשדרג את המנוי או לשמור פחות היסטוריה בתוכניות הגיבוי.',
  'See usage': 'צפייה בשימוש',
  'Unusual changes on {name}': 'שינויים חריגים ב-{name}',
  'Many files were changed or deleted at once. This can be a sign of ransomware. Older backups are kept safe for 14 days while you check. If you are not sure, contact your IT person.': 'הרבה קבצים שונו או נמחקו בבת אחת. זה יכול להיות סימן לתוכנת כופר. גיבויים ישנים יותר נשמרים בבטחה 14 יום כדי שאפשר יהיה לבדוק. במקרה של ספק, כדאי לפנות לאיש ה-IT.',
  'The automatic test restore failed for {name}': 'שחזור הניסיון האוטומטי נכשל ב-{name}',
  'An automatic check of the backups failed for {name}': 'בדיקה אוטומטית של הגיבויים נכשלה ב-{name}',
  'Please contact support so we can look into it.': 'כדאי לפנות לתמיכה כדי שנבדוק את זה.',
  '{time}, {n} times': '{time}, {n} פעמים',

  // ───────── overview ─────────
  '{value} of {total} computers backed up': '{value} מתוך {total} מחשבים מגובים',
  'Backup requested. The computer starts within a minute.': 'הגיבוי התבקש. המחשב יתחיל תוך דקה.',
  'Confirm your email address': 'אישור כתובת האימייל',
  'We sent the email again.': 'האימייל נשלח שוב.',
  'Connect your first computer': 'חיבור המחשב הראשון',
  'Connect a computer': 'חיבור מחשב',
  'Check what gets backed up': 'בדיקה של מה מגובה',
  'Check the plan': 'בדיקת התוכנית',
  'Create a plan': 'יצירת תוכנית',
  'First backup finished': 'הגיבוי הראשון הסתיים',
  'Try getting a file back': 'ניסיון לשחזר קובץ',
  'Get a file back': 'שחזור קובץ',
  "Let's protect your first computer": 'בואו נגן על המחשב הראשון',
  'Your computer is backed up': 'המחשב מגובה',
  'All {n} computers are backed up': 'כל {n} המחשבים מגובים',
  '1 computer needs attention': 'מחשב אחד דורש טיפול',
  '{n} computers need attention': '{n} מחשבים דורשים טיפול',
  'We could not prepare your backup storage': 'לא הצלחנו להכין את אחסון הגיבוי',
  'Our team has been notified. Please contact support if this does not clear up within an hour.': 'הצוות שלנו קיבל הודעה. אם זה לא מסתדר תוך שעה, כדאי לפנות לתמיכה.',
  'Preparing your private backup storage…': 'מכינים את אחסון הגיבוי הפרטי…',
  'This takes a minute or two. You can already connect computers.': 'זה לוקח דקה או שתיים. כבר אפשר לחבר מחשבים.',
  'Connect a computer and it starts backing up on its own. It takes about five minutes.': 'מחשב שמחובר מתחיל לגבות לבד. זה לוקח בערך חמש דקות.',
  '{n} still setting up': '{n} עדיין בהכנה',
  '{n} with backups paused': '{n} עם גיבויים מושהים',
  '{n} blocked': '{n} חסומים',
  '{n} backing up right now': '{n} מגבים ממש עכשיו',
  'Backups run automatically. There is nothing you need to do.': 'הגיבויים רצים אוטומטית. אין צורך לעשות דבר.',
  'Still setting up': 'עדיין בהכנה',
  'Make sure the computer is on and connected to the internet. If this does not clear up, contact support.': 'יש לוודא שהמחשב דולק ומחובר לאינטרנט. אם זה לא מסתדר, כדאי לפנות לתמיכה.',
  'Open the plan': 'פתיחת התוכנית',
  'backups finished': 'גיבויים הסתיימו',
  "{n} didn't finish": '{n} לא הסתיימו',
  'Storage used': 'אחסון בשימוש',
  'of {gb} GB in your subscription': 'מתוך ⁦{gb} GB⁩ במנוי',
  'Automatic checks': 'בדיקות אוטומטיות',
  'A check found a problem.': 'בדיקה מצאה בעיה.',
  'See alerts': 'צפייה בהתראות',
  'Your backups are tested automatically and can be restored.': 'הגיבויים נבדקים אוטומטית ואפשר לשחזר מהם.',
  'Checks start automatically after the first backups.': 'הבדיקות מתחילות אוטומטית אחרי הגיבויים הראשונים.',
  'No alerts': 'אין התראות',
  'You can see everything here, but only admins can make changes.': 'כאן אפשר לראות הכול, אבל רק מנהלים יכולים לבצע שינויים.',

  // ───────── computers ─────────
  'Every computer you connect is backed up automatically, following its backup plan.': 'כל מחשב שמחובר מגובה אוטומטית, לפי תוכנית הגיבוי שלו.',
  'Search by name…': 'חיפוש לפי שם…',
  'Change backup plan ({n})': 'שינוי תוכנית גיבוי ({n})',
  'Nothing needs attention': 'אין מה לטפל',
  'No removed computers': 'אין מחשבים שהוסרו',
  'Backup plan': 'תוכנית גיבוי',
  'Size of files': 'גודל הקבצים',
  'Windows installer': 'התקנה ל-Windows',
  'Ubuntu or Debian (.deb)': 'Ubuntu או Debian (‎.deb)',
  'Red Hat, Fedora or Rocky (.rpm)': 'Red Hat, Fedora או Rocky (‎.rpm)',
  'Program file only (for IT staff)': 'קובץ התוכנה בלבד (לאנשי IT)',
  'Archive (for IT staff)': 'קובץ ארכיון (לאנשי IT)',
  'Download ZeroGap for Windows': 'הורדת ZeroGap ל-Windows',
  'Download: {kind}': 'הורדה: {kind}',
  'Do this on the computer you want to back up. It takes about five minutes.': 'את זה עושים במחשב שרוצים לגבות. זה לוקח בערך חמש דקות.',
  'Please confirm your email address first': 'קודם יש לאשר את כתובת האימייל',
  'We sent you an email with a link. After you click it, come back here.': 'שלחנו אימייל עם קישור. אחרי הלחיצה עליו, אפשר לחזור לכאן.',
  'Your subscription is full': 'המנוי מלא',
  'It allows {n} computers. Remove a computer you no longer use, or upgrade the subscription.': 'המנוי מאפשר {n} מחשבים. אפשר להסיר מחשב שכבר לא בשימוש, או לשדרג את המנוי.',
  'See subscription': 'צפייה במנוי',
  '1. Download ZeroGap': '1. הורדת ZeroGap',
  'The installer is not available on this server yet. Please contact support.': 'קובץ ההתקנה עדיין לא זמין בשרת הזה. כדאי לפנות לתמיכה.',
  'Download the installer on the computer you want to back up, then open it.': 'יש להוריד את קובץ ההתקנה במחשב שרוצים לגבות, ואז לפתוח אותו.',
  'Download the package that fits the computer, then install it.': 'יש להוריד את החבילה שמתאימה למחשב, ואז להתקין אותה.',
  'Is it a Linux computer?': 'זה מחשב Linux?',
  'Is it a Windows computer?': 'זה מחשב Windows?',
  '2. Enter the connection code': '2. הזנת קוד החיבור',
  'The installer asks for a connection code. Create it here.': 'במהלך ההתקנה יתבקש קוד חיבור. אפשר ליצור אותו כאן.',
  'Backup plan for this computer': 'תוכנית הגיבוי למחשב הזה',
  'What gets backed up and when. You can change it later on the computer’s page.': 'מה מגובה ומתי. אפשר לשנות את זה אחר כך בדף המחשב.',
  'Create a connection code': 'יצירת קוד חיבור',
  'When the installer asks, copy these two lines into it:': 'כשההתקנה מבקשת, יש להעתיק אליה את שתי השורות האלה:',
  'Server address': 'כתובת השרת',
  'When the installer asks, copy this code into it:': 'כשההתקנה מבקשת, יש להעתיק אליה את הקוד הזה:',
  'Check that the code was copied exactly, with nothing missing.': 'יש לוודא שהקוד הועתק במדויק, בלי שחסר משהו.',
  'Downloaded': 'הורד',
  'Connection code': 'קוד חיבור',
  'The code works for one computer and expires {time}. Keep it private.': 'הקוד מתאים למחשב אחד ותוקפו פג ב-{time}. אין לשתף אותו.',
  '3. Waiting for the computer': '3. ממתינים למחשב',
  'Waiting for the computer to connect… This page updates by itself.': 'ממתינים שהמחשב יתחבר… הדף מתעדכן לבד.',
  'Taking longer than usual?': 'לוקח יותר זמן מהרגיל?',
  'Check that the computer is connected to the internet.': 'יש לוודא שהמחשב מחובר לאינטרנט.',
  'Check that the server address and code were copied exactly, with nothing missing.': 'יש לוודא שכתובת השרת והקוד הועתקו במדויק, בלי שחסר משהו.',
  'If the installer finished without asking for the code, create a new code and run the installer again.': 'אם ההתקנה הסתיימה בלי לבקש קוד, יש ליצור קוד חדש ולהריץ את ההתקנה שוב.',
  '{name} is connected. Getting it ready…': '{name} מחובר. מכינים אותו…',
  '{name} is connected and protected by “{plan}”.': '{name} מחובר ומוגן לפי "{plan}".',
  'Its first backup starts on its own. You can also start it right away.': 'הגיבוי הראשון שלו יתחיל לבד. אפשר גם להתחיל אותו מיד.',
  'Start the first backup now': 'התחלת הגיבוי הראשון עכשיו',
  'Connect another computer': 'חיבור מחשב נוסף',
  'Connecting many computers at once? (for IT staff)': 'מחברים הרבה מחשבים בבת אחת? (לאנשי IT)',
  'Create one code that several computers can use, and install without questions, for example with Group Policy or a remote management tool.': 'אפשר ליצור קוד אחד לכמה מחשבים ולהתקין בלי שאלות, למשל עם Group Policy או כלי ניהול מרחוק.',
  'How many computers': 'כמה מחשבים',
  'Code expires after (hours, up to 72)': 'תוקף הקוד (שעות, עד 72)',
  'Create a code for {n} computers': 'יצירת קוד ל-{n} מחשבים',
  'Windows, silent install': 'Windows, התקנה שקטה',
  'Linux, after installing the package': 'Linux, אחרי התקנת החבילה',
  'Backup status': 'מצב הגיבוי',
  '{files} files checked · {uploaded} sent': '{files} קבצים נבדקו · {uploaded} נשלחו',
  'The backup will stop in a moment': 'הגיבוי ייעצר בעוד רגע',
  'Stop this backup': 'עצירת הגיבוי',
  'Last backup': 'הגיבוי האחרון',
  'Not yet': 'עדיין לא',
  'Next backup': 'הגיבוי הבא',
  'Size of backed-up files': 'גודל הקבצים שגובו',
  'Saved backups': 'גיבויים שמורים',
  'Change plan': 'שינוי תוכנית',
  'The computer picks up plan changes the next time it connects.': 'המחשב יקבל את שינויי התוכנית בפעם הבאה שהוא מתחבר.',
  'This computer has no backup plan, so it is not being backed up.': 'למחשב הזה אין תוכנית גיבוי, ולכן הוא לא מגובה.',
  'This computer is blocked': 'המחשב הזה חסום',
  'It was reported lost or stolen {time}. It can no longer reach its backups, but you can still restore them to another computer.': 'דווח שהוא אבד או נגנב {time}. אין לו יותר גישה לגיבויים שלו, אבל עדיין אפשר לשחזר אותם למחשב אחר.',
  'History': 'היסטוריה',
  'Name shown in ZeroGap': 'השם שמוצג ב-ZeroGap',
  'Lost, stolen or no longer used': 'אבד, נגנב או לא בשימוש',
  'Its backups are always kept, and you can still restore them to another computer.': 'הגיבויים שלו תמיד נשמרים, ועדיין אפשר לשחזר אותם למחשב אחר.',
  'Lost or stolen? Block this computer…': 'אבד או נגנב? חסימת המחשב…',
  'Remove this computer…': 'הסרת המחשב…',
  'This computer was removed {time}.': 'המחשב הוסר {time}.',
  'Block {name}?': 'לחסום את {name}?',
  'Block the computer': 'חסימת המחשב',
  'Reported lost or stolen': 'דווח שאבד או נגנב',
  'Computer blocked': 'המחשב נחסם',
  'Use this if the computer was lost or stolen. It immediately loses all access to its backups and stops backing up. Its backups stay safe and can be restored to another computer.': 'לשימוש כשהמחשב אבד או נגנב. הוא מאבד מיד כל גישה לגיבויים שלו ומפסיק לגבות. הגיבויים שלו נשארים בטוחים ואפשר לשחזר אותם למחשב אחר.',
  'What happened? (optional)': 'מה קרה? (לא חובה)',
  'Remove {name}?': 'להסיר את {name}?',
  'Remove the computer': 'הסרת המחשב',
  'Computer removed': 'המחשב הוסר',
  'Use this when a computer is replaced or no longer used. It stops backing up and frees its place in your subscription. Its backups are kept until they expire under its backup plan.': 'לשימוש כשמחשב מוחלף או לא בשימוש. הוא מפסיק לגבות ומפנה את מקומו במנוי. הגיבויים שלו נשמרים עד שתוקפם יפוג לפי תוכנית הגיבוי שלו.',
  'Computer name on the network': 'שם המחשב ברשת',
  'ZeroGap app version': 'גרסת תוכנת ZeroGap',
  'Connected since': 'מחובר מאז',
  'Plan version in use': 'גרסת התוכנית שבשימוש',
  'Up to date': 'מעודכנת',
  'Waiting for the computer': 'ממתינים למחשב',
  'Storage access': 'גישה לאחסון',
  "Each computer has its own key that can reach only its own backups, can't delete history, and is never shown in full.": 'לכל מחשב יש מפתח משלו, שמגיע רק לגיבויים שלו, לא יכול למחוק היסטוריה ואף פעם לא מוצג במלואו.',
  'A new key is being issued. The old one stops working once the computer confirms.': 'מונפק מפתח חדש. הישן יפסיק לעבוד אחרי שהמחשב יאשר.',
  'Replace the key': 'החלפת המפתח',
  'Event log': 'יומן אירועים',
  'Last report from the computer': 'הדיווח האחרון מהמחשב',
  'No saved backups yet': 'עדיין אין גיבויים שמורים',
  'They appear here after the first backup finishes.': 'הם יופיעו כאן אחרי שהגיבוי הראשון יסתיים.',
  'Checked automatically': 'נבדק אוטומטית',
  'Some files were skipped': 'חלק מהקבצים דולגו',
  'Choose a backup to see its files': 'יש לבחור גיבוי כדי לראות את הקבצים שלו',
  'Scheduled': 'מתוזמן',
  'Started by hand': 'הופעל ידנית',
  'Catching up after the computer was off': 'השלמה אחרי שהמחשב היה כבוי',
  'While offline': 'בזמן ניתוק',
  'A file arrived': 'התקבל קובץ',
  'Routine check': 'בדיקה תקופתית',
  'Shared to ZeroGap': 'שותף ל־ZeroGap',
  'Finished, some files skipped': 'הסתיים, חלק מהקבצים דולגו',
  "Didn't finish": 'לא הסתיים',
  'Finishing': 'מסתיים',
  'Waiting to start': 'ממתין להתחלה',
  'Trying again': 'מנסה שוב',
  'Stopped': 'נעצר',
  "Didn't start": 'לא התחיל',
  'Why': 'סיבה',
  'Result': 'תוצאה',
  'Took': 'משך',
  'Why?': 'למה?',
  '{n} files could not be read': 'לא ניתן היה לקרוא {n} קבצים',
  '{n} sec': '{n} שנ׳',
  '{n} min': '{n} דק׳',
  '{h} h {m} min': '{h} שע׳ {m} דק׳',
  'See which': 'הצגת הקבצים',
  'See skipped files': 'הקבצים שדולגו',
  "{n} files couldn't be saved": '{n} קבצים לא נשמרו',
  "Files this backup couldn't save": 'קבצים שלא נשמרו בגיבוי הזה',
  '{n} items were skipped. Everything else in this backup was saved normally.': '{n} פריטים דולגו. כל השאר בגיבוי הזה נשמר כרגיל.',
  'File or folder': 'קובץ או תיקייה',
  'Why it was skipped': 'למה דולג',
  'The list of skipped files was not kept for this backup': 'רשימת הקבצים שדולגו לא נשמרה בגיבוי הזה',
  'Only the first {n} are listed.': 'מוצגים רק {n} הראשונים.',
  'Open in another program': 'פתוח בתוכנה אחרת',
  'A program kept it open. Many of these are system files that never need a backup. If one matters, close the program before the next backup.': 'תוכנה החזיקה אותו פתוח. רבים מהם קבצי מערכת שלא צריך לגבות. אם אחד מהם חשוב, כדאי לסגור את התוכנה לפני הגיבוי הבא.',
  'Not allowed to open it': 'אין הרשאה לפתוח',
  'The computer did not let ZeroGap open it. These are usually system files or private files of other programs.': 'המחשב לא איפשר ל־ZeroGap לפתוח אותו. בדרך כלל אלה קבצי מערכת או קבצים פרטיים של תוכנות אחרות.',
  'Only stored online': 'שמור רק ברשת',
  'It lives in OneDrive or a similar service and was not downloaded to this computer. It is still in that online service.': 'הקובץ נמצא ב־OneDrive או בשירות דומה ולא הורד למחשב הזה. הוא עדיין שמור בשירות הזה.',
  'Deleted or moved during the backup': 'נמחק או הועבר בזמן הגיבוי',
  'It disappeared while the backup was running. Nothing to do.': 'הקובץ נעלם בזמן שהגיבוי רץ. אין צורך לעשות דבר.',
  'A folder in the plan is not on this computer': 'תיקייה מהתוכנית לא קיימת במחשב הזה',
  'The backup plan includes a folder this computer does not have. Nothing is lost.': 'תוכנית הגיבוי כוללת תיקייה שאין במחשב הזה. שום דבר לא אבד.',
  'The name has unusual characters': 'יש בשם תווים לא רגילים',
  'Renaming the file usually fixes this.': 'שינוי שם הקובץ בדרך כלל פותר את זה.',
  'Changed while it was being saved': 'השתנה בזמן השמירה',
  'The next backup tries again.': 'הגיבוי הבא ינסה שוב.',
  'The disk could not read it': 'הדיסק לא הצליח לקרוא אותו',
  'The disk may be starting to fail. Worth having it checked soon.': 'ייתכן שהדיסק מתחיל להתקלקל. כדאי לבדוק אותו בקרוב.',
  'Could not be read': 'לא ניתן היה לקרוא',
  'Open the details for the exact message.': 'ההודעה המדויקת מופיעה בפרטים.',

  // ───────── get files back ─────────
  'Search by file name…': 'חיפוש לפי שם קובץ…',
  'Search this backup': 'חיפוש בגיבוי הזה',
  'Last changed': 'שונה לאחרונה',
  'was being edited during the backup': 'נערך בזמן הגיבוי',
  'Earlier versions': 'גרסאות קודמות',
  'This folder is empty in this backup': 'התיקייה הזו ריקה בגיבוי הזה',
  'Show more': 'הצגת עוד',
  'Kept in backups from': 'שמור בגיבויים מ-',
  'Get this version back': 'שחזור הגרסה הזו',
  'Restore this file': 'שחזור הקובץ הזה',
  'Today': 'היום',
  'Yesterday': 'אתמול',
  'Whose files': 'של מי הקבצים',
  'Which files': 'אילו קבצים',
  'Where to': 'לאן',
  'Check and start': 'בדיקה והתחלה',
  '{files} files ({size})': '{files} קבצים ({size})',
  'the chosen files': 'הקבצים שנבחרו',
  'download them as a ZIP file': 'להוריד אותם כקובץ ZIP',
  'put them back where they were on {name}': 'להחזיר אותם למקומם ב-{name}',
  'save them to {path} on {name}': 'לשמור אותם ב-{path} ב-{name}',
  'Get {what} from {name} as they were on {when}, and {where}.': 'לשחזר {what} מ-{name} כפי שהיו ב-{when}, ו{where}.',
  'Getting your files back…': 'משחזרים את הקבצים…',
  'You can look through what is backed up, but only admins can get files back.': 'אפשר לעיין במה שמגובה, אבל רק מנהלים יכולים לשחזר קבצים.',
  'Previous restores': 'שחזורים קודמים',
  'Whose files do you want back?': 'של איזה מחשב הקבצים?',
  'Nothing to get back yet': 'עדיין אין מה לשחזר',
  'Files can be brought back after a computer finishes its first backup.': 'אפשר לשחזר קבצים אחרי שמחשב מסיים את הגיבוי הראשון שלו.',
  'Last backup {time}': 'גיבוי אחרון {time}',
  'Removed computer': 'מחשב שהוסר',
  'The computer does not need to be on. You can also get files from a computer that was removed, lost or broken.': 'המחשב לא צריך להיות דולק. אפשר לשחזר גם ממחשב שהוסר, אבד או התקלקל.',
  'some files were skipped': 'חלק מהקבצים דולגו',
  'Tick the files or folders you want. A ticked folder brings back everything inside it.': 'יש לסמן את הקבצים או התיקיות הרצויים. תיקייה מסומנת משחזרת את כל מה שבתוכה.',
  '1 item chosen': 'נבחר פריט אחד',
  '{n} items chosen': 'נבחרו {n} פריטים',
  '{files} files in total, {size}': 'בסך הכול {files} קבצים, {size}',
  'Counting…': 'סופרים…',
  'Tick at least one file or folder.': 'יש לסמן לפחות קובץ או תיקייה אחת.',
  'Download them as a ZIP file': 'הורדה כקובץ ZIP',
  'Best for a few files. The download is ready in a few minutes (up to 10 GB).': 'הכי מתאים לכמה קבצים. ההורדה מוכנה תוך כמה דקות (עד 10 GB).',
  'Save them in a new folder on {name}': 'שמירה בתיקייה חדשה ב-{name}',
  'Nothing on the computer is replaced. The computer must be on.': 'שום דבר במחשב לא מוחלף. המחשב צריך להיות דולק.',
  'Folder': 'תיקייה',
  'Put them back where they were': 'החזרה למקום המקורי',
  'If a file with the same name is there, both are kept: the restored copy gets “(restored)” in its name.': 'אם כבר יש שם קובץ באותו שם, שניהם נשמרים: לעותק המשוחזר נוסף "(restored)" לשם.',
  'Send them to another computer': 'שליחה למחשב אחר',
  'For example, a new computer that replaces a broken one.': 'למשל מחשב חדש שמחליף מחשב שהתקלקל.',
  'More options': 'אפשרויות נוספות',
  'If a file with the same name is already there': 'אם כבר קיים קובץ באותו שם',
  'Keep both (recommended)': 'שמירת שניהם (מומלץ)',
  'Leave it, skip the restored one': 'להשאיר אותו ולדלג על המשוחזר',
  'Replace it': 'להחליף אותו',
  'I understand that files already there will be replaced by the restored versions.': 'ברור לי שקבצים שכבר קיימים שם יוחלפו בגרסאות המשוחזרות.',
  'Keep the original "last changed" dates': 'שמירת תאריכי השינוי המקוריים',
  'Also bring back who may open the files (file permissions)': 'שחזור גם של מי רשאי לפתוח את הקבצים (הרשאות)',
  'Chosen files and folders': 'הקבצים והתיקיות שנבחרו',
  'Files already there will be replaced.': 'קבצים שכבר קיימים שם יוחלפו.',
  'Every file is checked to be exactly as it was backed up before it is saved.': 'כל קובץ נבדק לפני השמירה כדי לוודא שהוא בדיוק כמו שגובה.',
  'Start': 'התחלה',
  // ───────── getting files back, files first (docs/restore-files-first.md) ─────────
  'Bring back files or folders, even if the computer is off or gone. Each file comes back as its newest copy unless you choose an older one.': 'החזרת קבצים או תיקיות, גם אם המחשב כבוי או כבר לא קיים. כל קובץ חוזר כעותק החדש ביותר שלו, אלא אם בוחרים עותק קודם.',
  'Files that are no longer on the computer, most recent first': 'קבצים שכבר לא נמצאים במחשב, האחרונים קודם',
  'Search results': 'תוצאות חיפוש',
  'Starting at': 'מתוך',
  'Only deleted files': 'רק קבצים שנמחקו',
  'Search everything that is kept': 'חיפוש בכל מה שנשמר',
  'Files and folders': 'קבצים ותיקיות',
  'No deleted files are kept': 'אין קבצים שנמחקו ועדיין שמורים',
  'Arrow keys move and open folders, Space ticks, Enter shows older copies.': 'החצים זזים ופותחים תיקיות, רווח מסמן, Enter מציג עותקים קודמים.',
  'Nothing is kept for this computer yet': 'עדיין לא נשמר שום דבר מהמחשב הזה',
  'deleted on {date}': 'נמחק ב-{date}',
  'deleted': 'נמחק',
  'Copy from {date}': 'עותק מ-{date}',
  'Older versions ({n})': 'גרסאות קודמות ({n})',
  'Copies of {name}': 'העותקים של {name}',
  'Newest': 'החדש ביותר',
  'Saved {time}': 'נשמר {time}',
  'As it was on which day?': 'כפי שהיה באיזה יום?',
  'Each day shows its last backup. The files will show exactly as they were then, and only what is ticked comes back.': 'לכל יום מוצג הגיבוי האחרון שלו. הקבצים יוצגו בדיוק כפי שהיו אז, ורק מה שמסומן ישוחזר.',
  'Other times that day ({n})': 'שעות אחרות באותו יום ({n})',
  'Show earlier days': 'הצגת ימים מוקדמים יותר',
  'the newest copies': 'העותקים החדשים ביותר',
  'the copy from {date}': 'העותק מ-{date}',
  'copies from earlier backups': 'עותקים מגיבויים קודמים',
  'the newest copies, and 1 file from {date}': 'העותקים החדשים ביותר, וקובץ אחד מ-{date}',
  'the newest copies, and {n} files from earlier backups': 'העותקים החדשים ביותר, ו-{n} קבצים מגיבויים קודמים',
  'Get {what} from {name} ({copies}), and {where}.': 'לשחזר {what} מ-{name} ({copies}), ו{where}.',
  'Showing files as they were on {time}': 'מוצגים הקבצים כפי שהיו ב-{time}',
  'Only what you tick comes back, exactly as it was then.': 'רק מה שמסומן ישוחזר, בדיוק כפי שהיה אז.',
  'Back to newest': 'חזרה לחדשים ביותר',
  'Bring a folder back as it was on a certain day': 'החזרת תיקייה כפי שהייתה ביום מסוים',
  'Many of these files changed on {date}': 'רבים מהקבצים האלה השתנו ב-{date}',
  'If something damaged them, get them as they were before that.': 'אם משהו פגע בהם, אפשר לשחזר אותם כפי שהיו לפני כן.',
  'Show files as they were on {date}': 'הצגת הקבצים כפי שהיו ב-{date}',
  'Too many separate items. Tick their folder instead.': 'יותר מדי פריטים נפרדים. עדיף לסמן את התיקייה שלהם.',
  '1 older copy chosen': 'נבחר עותק קודם אחד',
  '{n} older copies chosen': 'נבחרו {n} עותקים קודמים',
  'Choose a day instead?': 'לבחור יום במקום?',
  'Back to the newest copies?': 'לחזור לעותקים החדשים ביותר?',
  'Choose a day': 'בחירת יום',
  'What you ticked so far will be cleared, and you can tick again.': 'הסימונים עד עכשיו יתאפסו, ואפשר יהיה לסמן מחדש.',
  'Newest copies and some older ones': 'העותקים החדשים ביותר וכמה עותקים קודמים',
  'Newest copies': 'העותקים החדשים ביותר',
  'requested {requested}': 'התבקש {requested}',
  'In progress': 'בתהליך',
  'Partly done': 'הושלם חלקית',
  "Didn't work": 'לא הצליח',
  'ZIP download': 'הורדת ZIP',
  'Back where they were': 'למקום המקורי',
  'Trying to get a file back once is the best way to know your backups work.': 'לנסות לשחזר קובץ פעם אחת זו הדרך הטובה ביותר לדעת שהגיבויים עובדים.',
  'ZeroGap support': 'התמיכה של ZeroGap',
  'Your download is ready': 'ההורדה מוכנה',
  'Done: all {n} files are back': 'הסתיים: כל {n} הקבצים שוחזרו',
  'Finished, but {n} files could not be brought back': 'הסתיים, אבל {n} קבצים לא שוחזרו',
  "The restore didn't work": 'השחזור לא הצליח',
  'The restore was canceled': 'השחזור בוטל',
  'The restore did not finish in time': 'השחזור לא הסתיים בזמן',
  'Waiting for the computer to start…': 'ממתינים שהמחשב יתחיל…',
  'Getting your files back… {pct}%': 'משחזרים את הקבצים… {pct}%',
  'Files from {name}': 'קבצים מ-{name}',
  'As they were on {time} · requested {requested}': 'כפי שהיו ב-{time} · התבקש {requested}',
  'Trying the missing files again': 'מנסים שוב את הקבצים החסרים',
  'Try the missing files again': 'ניסיון חוזר לקבצים החסרים',
  '{done} of {total} files · {size}': '{done} מתוך {total} קבצים · {size}',
  '{n} skipped because a file with that name was already there': '{n} דולגו כי כבר היה שם קובץ באותו שם',
  'Download the ZIP file': 'הורדת קובץ ה-ZIP',
  'Available until {time}': 'זמין עד {time}',
  'Where:': 'לאן:',
  'Problems only': 'רק בעיות',
  'Could not bring back': 'לא שוחזר',
  'Waiting': 'ממתין',
  'No files yet': 'עדיין אין קבצים',

  // ───────── team ─────────
  'View only': 'צפייה בלבד',
  'Can change everything: computers, backup plans, getting files back, people and the subscription.': 'אפשר לשנות הכול: מחשבים, תוכניות גיבוי, שחזור קבצים, אנשים והמנוי.',
  'Can see how backups are doing and which files are backed up, but cannot change anything or get files back.': 'אפשר לראות את מצב הגיבויים ואילו קבצים מגובים, אבל לא לשנות דבר או לשחזר קבצים.',
  'People who can sign in to ZeroGap for your organization.': 'האנשים שיכולים להתחבר ל-ZeroGap בשם הארגון.',
  'Invite someone': 'הזמנת משתמש',
  'Person': 'משתמש',
  'Can do': 'הרשאה',
  'Two-step sign-in': 'אימות דו-שלבי',
  'Invited {sent} · the invitation expires {expires}': 'הוזמן {sent} · תוקף ההזמנה פג {expires}',
  'Invited as {role}': 'הוזמן: {role}',
  'Invitation canceled': 'ההזמנה בוטלה',
  'Cancel invitation': 'ביטול ההזמנה',
  'Remove {email}?': 'להסיר את {email}?',
  'Removed from the organization': 'הוסר מהארגון',
  'They will no longer be able to sign in to this organization. Nothing else changes.': 'לא תהיה יותר אפשרות להתחבר לארגון הזה עם החשבון הזה. שום דבר אחר לא משתנה.',
  'What can they do?': 'מה מותר לעשות?',

  // ───────── activity (noun phrases: the log reads "who · what") ─────────
  'signed in': 'התחברות',
  'signed out': 'התנתקות',
  'changed their password': 'שינוי סיסמה',
  'reset their password': 'איפוס סיסמה',
  'turned on two-step sign-in': 'הפעלת אימות דו-שלבי',
  'turned off two-step sign-in': 'כיבוי אימות דו-שלבי',
  'confirmed their email address': 'אישור כתובת אימייל',
  'tried something they are not allowed to do': 'ניסיון לפעולה ללא הרשאה',
  'started a backup by hand': 'הפעלת גיבוי ידנית',
  'stopped a backup': 'עצירת גיבוי',
  'finished a backup': 'סיום גיבוי',
  'connected a new computer': 'חיבור מחשב חדש',
  'computer is ready': 'המחשב מוכן',
  'changed a computer': 'שינוי מחשב',
  'changed several computers': 'שינוי כמה מחשבים',
  'blocked a computer': 'חסימת מחשב',
  'removed a computer': 'הסרת מחשב',
  'replaced a computer’s storage key': 'החלפת מפתח האחסון של מחשב',
  'asked for a new storage key': 'בקשת מפתח אחסון חדש',
  'created a connection code': 'יצירת קוד חיבור',
  'canceled a connection code': 'ביטול קוד חיבור',
  'created a backup plan': 'יצירת תוכנית גיבוי',
  'changed a backup plan': 'שינוי תוכנית גיבוי',
  'moved computers to a backup plan': 'העברת מחשבים לתוכנית גיבוי',
  'chose the default backup plan': 'בחירת תוכנית ברירת המחדל',
  'paused backups for a plan': 'השהיית גיבויים בתוכנית',
  'resumed backups for a plan': 'חידוש גיבויים בתוכנית',
  'deleted a backup plan': 'מחיקת תוכנית גיבוי',
  'started getting files back': 'התחלת שחזור קבצים',
  'downloaded restored files': 'הורדת קבצים משוחזרים',
  'canceled getting files back': 'ביטול שחזור קבצים',
  'retried getting files back': 'ניסיון חוזר לשחזור קבצים',
  'finished getting files back': 'סיום שחזור קבצים',
  'invited someone': 'הזמנת משתמש',
  'joined the organization': 'הצטרפות לארגון',
  'removed someone': 'הסרת משתמש',
  'changed what someone can do': 'שינוי הרשאה של משתמש',
  'canceled an invitation': 'ביטול הזמנה',
  'created the organization': 'יצירת הארגון',
  'changed the organization settings': 'שינוי הגדרות הארגון',
  'asked to delete the organization': 'בקשה למחוק את הארגון',
  'canceled deleting the organization': 'ביטול מחיקת הארגון',
  'connected another tool': 'חיבור כלי חיצוני',
  'disconnected a tool': 'ניתוק כלי חיצוני',
  'ZeroGap support started helping': 'התמיכה של ZeroGap התחילה לעזור',
  'ZeroGap support finished helping': 'התמיכה של ZeroGap סיימה לעזור',
  'old backups were removed as planned': 'גיבויים ישנים נמחקו לפי התוכנית',
  'backup storage is ready': 'אחסון הגיבוי מוכן',
  'ZeroGap support ({email})': 'התמיכה של ZeroGap ({email})',
  'Someone not signed in': 'משתמש לא מחובר',
  'Everything that happened in your organization: who signed in, what changed, and who got files back. This record cannot be edited.': 'כל מה שקרה בארגון: מי התחבר, מה השתנה ומי שחזר קבצים. אי אפשר לערוך את הרישום הזה.',
  'Show': 'הצגה',
  'Everything': 'הכול',
  'Getting files back': 'שחזור קבצים',
  'People': 'אנשים',
  'Sign-ins and security': 'התחברויות ואבטחה',
  'Blocked attempts': 'ניסיונות שנחסמו',
  'What happened': 'מה קרה',
  'blocked': 'נחסם',
  'failed': 'נכשל',
  'Affected: {email}': 'מושפע: {email}',

  // ───────── alerts & messages ─────────
  "A backup didn't finish": 'גיבוי לא הסתיים',
  'A computer has not been backed up for a while': 'מחשב לא גובה כבר זמן מה',
  'A computer has not connected for a long time': 'מחשב לא התחבר כבר הרבה זמן',
  'Your storage is almost full': 'האחסון כמעט מלא',
  'Unusual changes on a computer': 'שינויים חריגים במחשב',
  'An automatic check failed': 'בדיקה אוטומטית נכשלה',
  'Your files are back': 'הקבצים שוחזרו',
  "Getting files back didn't fully work": 'שחזור הקבצים לא הצליח במלואו',
  'Solved: backups are working again': 'נפתר: הגיבויים עובדים שוב',
  'Solved: the computer is connected again': 'נפתר: המחשב מחובר שוב',
  'Solved: the automatic check passed': 'נפתר: הבדיקה האוטומטית עברה',
  'Problems that need your attention. Most of them clear up on their own, and you get an email when something new happens.': 'בעיות שדורשות טיפול. רובן נפתרות מעצמן, ונשלח אימייל כשקורה משהו חדש.',
  'Old backups are being kept longer, until {time}': 'גיבויים ישנים נשמרים זמן רב יותר, עד {time}',
  'Because of unusual changes, nothing is removed from your history while you check.': 'בגלל שינויים חריגים, שום דבר לא נמחק מההיסטוריה בזמן הבדיקה.',
  'Everything is fine, go back to normal': 'הכול תקין, חזרה למצב רגיל',
  'Handled': 'טופל',
  'Messages ({n})': 'הודעות ({n})',
  'Messages': 'הודעות',
  'Nothing needs your attention': 'אין שום דבר שדורש טיפול',
  'Nothing here yet': 'עדיין אין כאן כלום',
  'Someone is already looking at this.': 'מישהו כבר מטפל בזה.',
  'Mark as handled': 'סימון כמטופל',
  'No messages': 'אין הודעות',
  'Messages sent to you, newest first.': 'הודעות שנשלחו אליך, מהחדשה לישנה.',
  'Mark all as read': 'סימון הכול כנקרא',

  // ───────── subscription & usage ─────────
  'Up to {devices} computers, {seats} people and {gb} GB of storage': 'עד {devices} מחשבים, {seats} משתמשים ו-⁦{gb} GB⁩ אחסון',
  'Download usage (CSV)': 'הורדת נתוני שימוש (CSV)',
  'Your subscription': 'המנוי שלך',
  'Free trial, ends {time}': 'תקופת ניסיון, מסתיימת {time}',
  'How you pay': 'אופן התשלום',
  'By invoice from your account manager. Nothing is charged automatically.': 'בחשבונית ממנהל החשבון. שום דבר לא מחויב אוטומטית.',
  'By card, every month.': 'בכרטיס אשראי, כל חודש.',
  'A payment is needed': 'נדרש תשלום',
  'Backups continue until {time}. Your existing backups are never deleted because of a missed payment.': 'הגיבויים ממשיכים עד {time}. גיבויים קיימים אף פעם לא נמחקים בגלל תשלום שלא בוצע.',
  'Storage used over the last 90 days': 'אחסון בשימוש ב-90 הימים האחרונים',
  'Nothing to show yet': 'עדיין אין מה להציג',
  'Storage used over time': 'אחסון בשימוש לאורך זמן',
  'Storage used is smaller than the size of your files, because files that repeat are stored once and everything is compressed.': 'האחסון בשימוש קטן מגודל הקבצים, כי קבצים שחוזרים על עצמם נשמרים פעם אחת והכול דחוס.',
  'By computer': 'לפי מחשב',
  'Subscriptions': 'מנויים',
  'Your current subscription': 'המנוי הנוכחי',
  'Switch to this': 'מעבר למנוי הזה',

  // ───────── settings ─────────
  'Used for reports and emails.': 'משמש לדוחות ולאימיילים.',
  'Admins must use two-step sign-in (a code from a phone app)': 'מנהלים חייבים להשתמש באימות דו-שלבי (קוד מאפליקציה בטלפון)',
  'How your backups are protected': 'איך הגיבויים מוגנים',
  'Files are encrypted on each computer before they are sent.': 'הקבצים מוצפנים בכל מחשב לפני שהם נשלחים.',
  'Deletion protection is off.': 'ההגנה מפני מחיקה כבויה.',
  'Backups cannot be deleted or changed for {days} days after they are made, even by someone who has your password.': 'אי אפשר למחוק או לשנות גיבויים במשך {days} ימים מרגע יצירתם, גם עם הסיסמה שלך.',
  'Each computer can reach only its own backups.': 'כל מחשב מגיע רק לגיבויים שלו.',
  'If a computer is lost, ZeroGap can still restore its files for you.': 'אם מחשב אבד, ZeroGap עדיין יכולה לשחזר את הקבצים שלו.',
  'Storage provider': 'ספק האחסון',
  'ZeroGap keeps recovery keys, protected by a key-management service, so it can restore files when a computer is lost. This means the service is not "zero-knowledge".': 'ZeroGap שומרת מפתחות שחזור, מוגנים בשירות לניהול מפתחות, כדי שאפשר יהיה לשחזר קבצים כשמחשב אבד. לכן השירות אינו "zero-knowledge".',
  'Delete the organization': 'מחיקת הארגון',
  'The organization will be deleted on {time}': 'הארגון יימחק ב-{time}',
  'Until then everything keeps working and you can still change your mind.': 'עד אז הכול ממשיך לעבוד ועדיין אפשר להתחרט.',
  'Keep the organization': 'השארת הארגון',
  'Deletes all computers, backups and settings after 30 days. Until then you can change your mind. To confirm, type the organization name.': 'מוחק את כל המחשבים, הגיבויים וההגדרות אחרי 30 יום. עד אז אפשר להתחרט. לאישור יש להקליד את שם הארגון.',
  'The organization will be deleted in 30 days': 'הארגון יימחק בעוד 30 יום',
  'Storage is almost full': 'האחסון כמעט מלא',
  'Getting files back finished': 'שחזור קבצים הסתיים',
  'The ZeroGap app was updated': 'תוכנת ZeroGap עודכנה',
  'Emails I get': 'האימיילים שלי',
  'Email me when something needs attention': 'לשלוח אימייל כשמשהו דורש טיפול',
  'Summary email': 'אימייל סיכום',
  'Every day': 'כל יום',
  'Every week': 'כל שבוע',
  'These settings are only for you. Everyone in the team chooses their own.': 'ההגדרות האלה הן רק עבורך. כל משתמש בצוות בוחר לעצמו.',
  'Connect other tools (for IT staff)': 'חיבור למערכות אחרות (לאנשי IT)',
  'Webhooks: send events to other systems': 'Webhooks: שליחת אירועים למערכות אחרות',
  'Copy the signing secret now. It is not shown again.': 'יש להעתיק את סוד החתימה עכשיו. הוא לא יוצג שוב.',
  'Working': 'עובד',
  'Turned off': 'כבוי',
  'Test event sent': 'אירוע בדיקה נשלח',

  // ───────── account, sign-in, tour ─────────
  'Two-step sign-in turned off': 'האימות הדו-שלבי כובה',
  'Open an authenticator app on your phone (such as Google Authenticator or Authy), scan this code, then type the 6 digits it shows.': 'יש לפתוח אפליקציית אימות בטלפון (כמו Google Authenticator או Authy), לסרוק את הקוד, ואז להקליד את 6 הספרות שמופיעות.',
  'Recommended: even if someone learns your password, they cannot sign in without your phone.': 'מומלץ: גם מי שמגלה את הסיסמה לא יוכל להתחבר בלי הטלפון שלך.',
  'At least 12 characters. You will be signed out on your other devices.': 'לפחות 12 תווים. ההתחברות במכשירים האחרים תנותק.',
  'Where you are signed in': 'איפה החשבון מחובר',
  'This device': 'המכשיר הזה',
  'Simple, safe backups for the computers in your business.': 'גיבוי פשוט ובטוח למחשבים של העסק.',
  'Backs up your computers automatically, several times a day': 'מגבה את המחשבים אוטומטית, כמה פעמים ביום',
  'Get any file back as it was, even if the computer is lost': 'אפשר לשחזר כל קובץ כפי שהיה, גם אם המחשב אבד',
  'Files are encrypted before they leave the computer': 'הקבצים מוצפנים לפני שהם יוצאים מהמחשב',
  'Welcome to ZeroGap': 'ברוכים הבאים ל-ZeroGap',
  'A one-minute look around. You can replay this tour any time from the bottom of the menu.': 'סיור קצר של דקה. אפשר לחזור עליו בכל עת מתחתית התפריט.',
  'Everything at a glance': 'הכול במבט אחד',
  'Here you see whether all your computers are backed up, and what to do if one needs attention.': 'כאן רואים אם כל המחשבים מגובים, ומה לעשות אם אחד מהם דורש טיפול.',
  'Start here: install ZeroGap on each computer you want to protect. It starts backing up on its own.': 'מתחילים כאן: מתקינים את ZeroGap בכל מחשב שרוצים להגן עליו. הוא מתחיל לגבות לבד.',
  'All your computers, whether each one is backed up, and when it was last backed up.': 'כל המחשבים, האם כל אחד מגובה ומתי גובה לאחרונה.',
  'A backup plan decides what gets backed up, when, and for how long. New computers get the default plan, so you only need to change it if you want to.': 'תוכנית גיבוי קובעת מה מגובה, מתי ולכמה זמן. מחשבים חדשים מקבלים את תוכנית ברירת המחדל, כך שצריך לשנות אותה רק אם רוצים.',
  'Bring back a file or folder as it was at an earlier backup, even if the computer is off or gone.': 'החזרת קובץ או תיקייה כפי שהיו בגיבוי קודם, גם אם המחשב כבוי או כבר לא קיים.',
  'See what is backed up. Admins can get files back from here.': 'כאן רואים מה מגובה. מנהלים יכולים לשחזר קבצים מכאן.',
  'If something needs your attention, it shows up here and you get an email.': 'אם משהו דורש טיפול, הוא מופיע כאן ונשלח גם אימייל.',
  // ───────── connecting a phone (docs/mobile.md) ─────────
  'Connect a phone': 'חיבור טלפון',
  'Files you add to ZeroGap on a phone are kept safe, and can appear in a folder on a computer.': 'קבצים שמוסיפים ל־ZeroGap בטלפון נשמרים, ויכולים להופיע גם בתיקייה במחשב.',
  '1. Install ZeroGap on the phone': '1. התקנת ZeroGap בטלפון',
  'Search for "ZeroGap" in the App Store or Google Play, then open it.': 'מחפשים "ZeroGap" ב־App Store או ב־Google Play ופותחים את האפליקציה.',
  '2. Scan this code with the phone': '2. סריקת הקוד בטלפון',
  'Connected': 'מחובר',
  'is connected and will start saving files right away.': 'מחובר ומתחיל לשמור קבצים מיד.',
  'See this phone': 'הצגת הטלפון',
  'Connect another phone': 'חיבור טלפון נוסף',
  'The code is good for two minutes and can be used once.': 'הקוד תקף לשתי דקות וניתן לשימוש אחד בלבד.',
  'Show the code': 'הצגת הקוד',
  'The code expired': 'הקוד פג',
  'Show a new code': 'הצגת קוד חדש',
  'In the app, choose "Scan the code on my computer". This code disappears in {n} seconds.': 'באפליקציה בוחרים "סריקת קוד מהמחשב". הקוד ייעלם בעוד {n} שניות.',
  'If the camera will not work, type this in the app instead': 'אם המצלמה לא עובדת, אפשר להקליד את הקוד באפליקציה',
  '3. What the person will see': '3. מה רואים בטלפון',
  'The app opens on "My Files". Anything they put there - or share to ZeroGap from another app - is saved automatically.': 'האפליקציה נפתחת במסך "הקבצים שלי". כל מה שמוסיפים שם — או משתפים ל־ZeroGap מאפליקציה אחרת — נשמר אוטומטית.',
  'Administrators of this organization can see the files people keep there, and the app says so when it is set up.': 'מנהלי הארגון יכולים לראות את הקבצים שנשמרים שם, והאפליקציה מציינת זאת בהתחברות.',

  // ───────── the assistant ─────────
  'Ask ZeroGap': 'שאלו את ZeroGap',
  'Ask about this computer': 'שאלה על המחשב הזה',
  'Ask': 'שליחה',
  'Your question': 'השאלה שלכם',
  'Ask about your backups…': 'אפשר לשאול על הגיבויים…',
  'Thinking': 'בודק',
  'Ask about your backups in your own words. I can look things up, find a file you have lost, and offer to put it back — you always confirm before anything happens.': 'אפשר לשאול על הגיבויים במילים רגילות. אפשר לבדוק פרטים, לאתר קובץ שאבד ולקבל הצעה להחזיר אותו — שום דבר לא קורה לפני אישור.',
  'Answers come from your own backup records. Check anything important on the page it came from.': 'התשובות מבוססות על רישומי הגיבוי שלכם. כדאי לאמת דבר חשוב בדף שממנו הוא הגיע.',
  'Tell me about this computer.': 'ספר לי על המחשב הזה.',
  'Done — what happened?': 'בוצע — מה קרה?',
  'It did not work — why, and what can be done?': 'זה לא הצליח — למה, ומה אפשר לעשות?',
  'Is everything backed up?': 'הכל מגובה?',
  'Why did last night’s backup fail?': 'למה הגיבוי של אמש נכשל?',
  'I deleted a file — can you find it?': 'מחקתי קובץ — אפשר למצוא אותו?',
  'When did my laptop last back up?': 'מתי הנייד שלי גובה לאחרונה?',
  'Looked something up': 'בדיקה במערכת',
  'Checked how your organization is doing': 'בדיקת מצב הארגון',
  'Looked at your computers': 'עיון במחשבים שלכם',
  'Looked at that computer': 'עיון במחשב הזה',
  'Checked recent backups': 'בדיקת הגיבויים האחרונים',
  'Read that backup in full': 'קריאת הגיבוי במלואו',
  'Read what the computer reported': 'קריאת הדיווחים מהמחשב',
  'Checked your alerts': 'בדיקת ההתראות',
  'Looked at your backup plans': 'עיון בתוכניות הגיבוי',
  'Checked your storage': 'בדיקת נפח האחסון',
  'Listed the saved backups': 'הצגת הגיבויים השמורים',
  'Searched a saved backup': 'חיפוש בגיבוי שמור',
  'Opened a folder in a saved backup': 'פתיחת תיקייה בגיבוי שמור',
  'Looked for earlier versions': 'חיפוש גרסאות קודמות',
  'Worked out how much there is to get back': 'חישוב כמה יש להחזיר',
  'Checked your recoveries': 'בדיקת השחזורים',
  'Looked at that recovery': 'עיון בשחזור הזה',
  'Prepared a backup for you to confirm': 'הכנת גיבוי לאישורכם',
  'Prepared that for you to confirm': 'הכנת הפעולה לאישורכם',
  'Prepared the recovery for you to confirm': 'הכנת השחזור לאישורכם',
  'The assistant is switched off for this organization.': 'העוזר כבוי בארגון הזה.',
  'An administrator can switch it on in Settings. Using it sends the names of your files and folders — never their contents — to the service that powers it.': 'מנהל יכול להפעיל אותו בהגדרות. השימוש בו שולח את שמות הקבצים והתיקיות — לעולם לא את התוכן שלהם — לשירות שמפעיל אותו.',
  'An administrator of your organization can switch it on in Settings.': 'מנהל בארגון שלכם יכול להפעיל אותו בהגדרות.',
  'This organization has used all its assistant questions for this month.': 'הארגון ניצל החודש את כל השאלות לעוזר.',
  'Everything else keeps working as normal, and the allowance starts again next month.': 'כל השאר ממשיך לעבוד כרגיל, והמכסה מתחדשת בחודש הבא.',
  'The assistant is not available here.': 'העוזר אינו זמין כאן.',
  'Confirm before this happens': 'אישור לפני הביצוע',
  'No, thanks': 'לא, תודה',
  'This offer is out of date. Ask again and the details will be checked afresh.': 'ההצעה כבר לא עדכנית. אפשר לשאול שוב והפרטים ייבדקו מחדש.',
  'You said no to this.': 'הפעולה נדחתה.',
  'This did not work.': 'הפעולה לא הצליחה.',
  'Done.': 'בוצע.',
  'Carry out this action.': 'ביצוע הפעולה הזו.',
  'Back up {computer} now, without waiting for its usual time.': 'גיבוי של {computer} עכשיו, בלי לחכות למועד הרגיל.',
  'Stop the backup running on {computer}.': 'עצירת הגיבוי שרץ על {computer}.',
  'Everything sent so far is kept and reused next time.': 'כל מה שנשלח עד כה נשמר וישמש בפעם הבאה.',
  'Pause the plan “{plan}”, so {n} computers stop backing up until it is switched back on.': 'השהיית התוכנית “{plan}”, כך שגיבוי ייפסק ב־{n} מחשבים עד להפעלה מחדש.',
  'Backups already saved are not affected.': 'גיבויים שכבר נשמרו לא מושפעים.',
  'Switch the plan “{plan}” back on, so its {n} computers return to their schedule.': 'הפעלת התוכנית “{plan}” מחדש, כך ש־{n} המחשבים שלה יחזרו ללוח הזמנים.',
  'Mark the alert “{title}” as dealt with.': 'סימון ההתראה “{title}” כמטופלת.',
  'Mark the alert “{title}” as seen.': 'סימון ההתראה “{title}” כנצפתה.',
  'Stop this recovery.': 'עצירת השחזור הזה.',
  '{done} of {total} files have arrived; those stay where they are.': '{done} מתוך {total} קבצים כבר הגיעו, והם נשארים במקומם.',
  'Prepare {files} files ({size}) from {computer} as a single download.': 'הכנת {files} קבצים ({size}) מהמחשב {computer} להורדה אחת.',
  'Put {files} files ({size}) back on {computer}, in a new folder named after today.': 'החזרת {files} קבצים ({size}) למחשב {computer}, לתיקייה חדשה על שם התאריך היום.',
  'From the backup saved {time}.': 'מתוך הגיבוי שנשמר {time}.',
  'and {n} more': 'ועוד {n}',
  'Nothing on the computer changes. You get a file to download when it is ready.': 'שום דבר במחשב לא משתנה. כשהקובץ יהיה מוכן אפשר יהיה להוריד אותו.',
  'Nothing that is already on the computer is replaced. If a file with the same name is there, the recovered one is saved beside it.': 'שום קובץ שכבר נמצא במחשב לא יוחלף. אם קיים קובץ באותו שם, הקובץ שהוחזר יישמר לידו.',
  'Pause this plan': 'השהיית התוכנית',
  'Resume this plan': 'הפעלת התוכנית מחדש',
  'Update this alert': 'עדכון ההתראה',
  'Get these files back': 'החזרת הקבצים',
  'Stop this recovery': 'עצירת השחזור',
  'Let people ask ZeroGap questions in their own words': 'לאפשר לשאול את ZeroGap שאלות במילים רגילות',
  'Adds a helper that answers questions about your backups and can offer to start a backup or get a file back. It is shown the names of your files and folders — never what is inside them — and nothing happens until the person confirms it.': 'מוסיף עוזר שעונה על שאלות בנוגע לגיבויים ויכול להציע להפעיל גיבוי או להחזיר קובץ. מוצגים לו שמות הקבצים והתיקיות — לעולם לא מה שבתוכם — ושום דבר לא קורה לפני שהמשתמש מאשר.',
  'Prepare one file ({size}) from {computer} as a download.': 'הכנת קובץ אחד ({size}) מהמחשב {computer} להורדה.',
  'Put one file ({size}) back on {computer}, in a new folder named after today.': 'החזרת קובץ אחד ({size}) למחשב {computer}, לתיקייה חדשה על שם התאריך היום.',
  'Pause the plan “{plan}”, so its one computer stops backing up until it is switched back on.': 'השהיית התוכנית “{plan}”, כך שהמחשב היחיד שלה יפסיק לגבות עד להפעלה מחדש.',
  'Switch the plan “{plan}” back on, so its computer returns to its schedule.': 'הפעלת התוכנית “{plan}” מחדש, כך שהמחשב שלה יחזור ללוח הזמנים.',
  'Recoveries': 'שחזורים',
  'Open this computer': 'פתיחת המחשב הזה',
  'Is my email backed up?': 'הדואר האלקטרוני שלי מגובה?',
  'Checked what did not arrive': 'בדיקת מה שלא הגיע',
  'Checked which files were left out': 'בדיקת הקבצים שלא נכללו',
  'Looked for deleted files': 'חיפוש קבצים שנמחקו',
  'Searched every computer': 'חיפוש בכל המחשבים',
  'Read the activity record': 'עיון ברישום הפעילות',
  'Checked who can help': 'איתור מנהלי הארגון',
  'Read your notifications': 'עיון בהודעות שלכם',
  'Counted up the month': 'סיכום נתוני החודש',
  'Send the one file that did not arrive to {computer} again.': 'שליחה חוזרת של הקובץ היחיד שלא הגיע למחשב {computer}.',
  'Send the {n} files that did not arrive to {computer} again.': 'שליחה חוזרת של {n} הקבצים שלא הגיעו למחשב {computer}.',
  'Files that already arrived are left as they are.': 'קבצים שכבר הגיעו נשארים כפי שהם.',
  'Keep the backup of {computer} saved {time}, so it is never deleted on schedule.': 'הגנה ממחיקה על הגיבוי של {computer} שנשמר {time}, כך שלא יימחק לפי לוח הזמנים.',
  'Keep this backup of {computer}, so it is never deleted on schedule.': 'הגנה ממחיקה על הגיבוי הזה של {computer}, כך שלא יימחק לפי לוח הזמנים.',
  'Saved with the note: “{reason}”': 'נשמר עם ההערה: “{reason}”',
  'It stays until somebody chooses “Stop keeping” on that computer’s page. Kept backups count towards your storage.': 'ההגנה נשארת עד שמישהו בוחר “הסרת ההגנה ממחיקה” בדף של המחשב. גיבויים מוגנים נספרים בנפח האחסון.',
  'Stop deleting old backups for {n} days, until {date}, for every computer.': 'הפסקת המחיקה של גיבויים ישנים למשך {n} ימים, עד {date}, בכל המחשבים.',
  'New backups carry on as usual.': 'גיבויים חדשים ממשיכים כרגיל.',
  'Old backups take up storage while they are kept. You can go back to normal on the Overview page at any time.': 'גיבויים ישנים תופסים נפח אחסון כל עוד הם נשמרים. אפשר לחזור למצב הרגיל בכל עת בדף הסקירה.',
  'Keep this backup': 'הגנה על הגיבוי ממחיקה',
  'Keep old backups': 'שמירת הגיבויים הישנים',
  'Kept': 'מוגן ממחיקה',
  'Stop keeping': 'הסרת ההגנה ממחיקה',
  'Stop keeping this backup?': 'להסיר את ההגנה ממחיקה מהגיבוי?',
  'Read that backup plan': 'עיון בתוכנית הגיבוי',
  'Change the plan': 'שינוי התוכנית',
  'Make this plan': 'יצירת התוכנית',
  'Back up {x}': 'גיבוי של {x}',
  'Stop backing up {x}': 'הפסקת הגיבוי של {x}',
  'Back up everybody’s {x}': 'גיבוי של התיקייה {x} של כל המשתמשים',
  'Stop backing up everybody’s {x}': 'הפסקת הגיבוי של התיקייה {x} של כל המשתמשים',
  'Leave out {x}': 'השמטה של {x}',
  'Stop leaving out {x}': 'חזרה לגבות את {x}',
  'Leave out files matching {x}': 'השמטה של קבצים שתואמים ל־{x}',
  'Stop leaving out files matching {x}': 'חזרה לגבות קבצים שתואמים ל־{x}',
  'Leave out {x} files': 'השמטה של קבצי {x}',
  'Stop leaving out {x} files': 'חזרה לגבות קבצי {x}',
  'Keep about {span} of history': 'שמירת היסטוריה של כ־{span}',
  'Rename it to “{name}”': 'שינוי השם ל“{name}”',
  'The newest backup has one such file ({size}); it will no longer be backed up.': 'בגיבוי האחרון יש קובץ אחד כזה ({size}), והוא לא יגובה יותר.',
  'The newest backup has {files} such files ({size}); they will no longer be backed up.': 'בגיבוי האחרון יש {files} קבצים כאלה ({size}), והם לא יגובו יותר.',
  'From the next backup, what is left out is no longer saved. Backups already taken still have it.': 'מהגיבוי הבא, מה שהושמט לא יישמר. גיבויים שכבר נשמרו עדיין כוללים אותו.',
  'Change the plan “{plan}”. No computer uses it yet.': 'שינוי התוכנית “{plan}”. אף מחשב עדיין לא משתמש בה.',
  'Change the plan “{plan}”, for {computers}.': 'שינוי התוכנית “{plan}”, עבור {computers}.',
  'Make a new plan “{name}”, a copy of “{from}” with these changes:': 'יצירת תוכנית חדשה “{name}”, עותק של “{from}” עם השינויים האלה:',
  'Make a new plan “{name}”, a copy of “{from}”.': 'יצירת תוכנית חדשה “{name}”, עותק של “{from}”.',
  'Move {computers} onto it.': 'העברת {computers} אליה.',
  'The other computer stays on “{from}”, unchanged.': 'המחשב השני נשאר בתוכנית “{from}” ללא שינוי.',
  'The other {n} computers stay on “{from}”, unchanged.': '{n} המחשבים האחרים נשארים בתוכנית “{from}” ללא שינוי.',
  'Move {computer} from “{from}” to “{plan}”.': 'העברת {computer} מהתוכנית “{from}” לתוכנית “{plan}”.',
  'Put {computer} on “{plan}”.': 'שיוך {computer} לתוכנית “{plan}”.',
  'It will also back up:': 'יגובו בנוסף:',
  'It will no longer back up:': 'לא יגובו יותר:',
  'It applies from each computer’s next backup.': 'השינוי חל מהגיבוי הבא של כל מחשב.',
  'From then on it is treated like any other saved backup, and is deleted once it is older than the backup plan keeps them.':'מעכשיו הוא יטופל כמו כל גיבוי שמור אחר, ויימחק כשיהיה ישן יותר ממה שתוכנית הגיבוי שומרת.',

  // ───────── phones in the portal ─────────
  'Computers and phones': 'מחשבים וטלפונים',
  'My Files': 'הקבצים שלי',
  'Phone': 'טלפון',
  'Whose phone': 'של מי',
  '{name}’s phone': 'הטלפון של {name}',
  'Ask about this phone': 'שאלה על הטלפון הזה',
  'Files on this phone': 'הקבצים בטלפון',
  'Everything in “My Files” in the ZeroGap app is saved as soon as it is added. There is nothing to schedule.':
    'כל מה שנמצא ב״הקבצים שלי״ באפליקציית ZeroGap נשמר ברגע שהוא נוסף. אין מה לתזמן.',
  'Last saved': 'נשמר לאחרונה',
  'Deleted files': 'קבצים שנמחקו',
  'Sent to computers': 'נשלח למחשבים',
  'This phone is blocked': 'הטלפון הזה חסום',
  'It was reported lost or stolen {time}. It can no longer reach its files, but they can still be got back.':
    'הוא דווח כאבוד או גנוב {time}. אין לו יותר גישה לקבצים שלו, אבל עדיין אפשר לשחזר אותם.',
  'Recently deleted': 'נמחקו לאחרונה',
  'Files appear here once something is added in the app on the phone.': 'הקבצים יופיעו כאן אחרי שמשהו יתווסף באפליקציה בטלפון.',
  'Can be got back for {n} days': 'אפשר לשחזר במשך {n} ימים',
  'How long deleted files can be got back': 'כמה זמן אפשר לשחזר קבצים שנמחקו',
  'The same for every phone in the organization.': 'זהה לכל הטלפונים בארגון.',
  'Not sent to any computer. In the app, choose “Send to a computer”.': 'לא נשלח לאף מחשב. באפליקציה בוחרים ״שליחה למחשב״.',
  'Up to date as of {time}': 'עודכן {time}',
  'Nothing deleted recently': 'לא נמחק דבר לאחרונה',
  'Files deleted on the phone are listed here while they can still be got back.': 'קבצים שנמחקו בטלפון מופיעים כאן כל עוד אפשר לשחזר אותם.',
  'Deleted': 'נמחק',
  'After {time}': 'אחרי {time}',
  'Get it back': 'שחזור',
  'Lost, stolen or replaced': 'אבד, נגנב או הוחלף',
  'The files stay in ZeroGap and can still be got back, on a computer or a new phone.': 'הקבצים נשארים ב-ZeroGap ואפשר לשחזר אותם, למחשב או לטלפון חדש.',
  'Lost or stolen? Block this phone…': 'אבד או נגנב? חסימת הטלפון…',
  'Remove this phone…': 'הסרת הטלפון…',
  'This phone was removed {time}.': 'הטלפון הוסר {time}.',
  'Block the phone': 'חסימת הטלפון',
  'Phone blocked': 'הטלפון נחסם',
  'The phone immediately loses all access to its files in ZeroGap and stops saving new ones. The files already saved stay safe.':
    'הטלפון מאבד מיד כל גישה לקבצים שלו ב-ZeroGap ומפסיק לשמור קבצים חדשים. הקבצים שכבר נשמרו נשארים בטוחים.',
  'Remove the phone': 'הסרת הטלפון',
  'Phone removed': 'הטלפון הוסר',
  'Use this when a phone is replaced or no longer used. It stops saving files and frees its place. Deleted files stay recoverable for the usual time.':
    'לשימוש כשטלפון מוחלף או כבר לא בשימוש. הוא מפסיק לשמור קבצים ומפנה את מקומו. קבצים שנמחקו נשארים ניתנים לשחזור למשך הזמן הרגיל.',
  'Open ZeroGap on the phone with an internet connection. Files are saved by themselves.': 'כדאי לפתוח את ZeroGap בטלפון כשיש חיבור לאינטרנט. הקבצים נשמרים מעצמם.',
  'Its files are kept and can still be got back.': 'הקבצים שלו נשמרים ועדיין אפשר לשחזר אותם.',
  'Blocked because it was reported lost or stolen. Its files can still be got back.': 'חסום כי דווח כאבוד או גנוב. עדיין אפשר לשחזר את הקבצים שלו.',
  'The phone is connected and getting ready. This takes a minute.': 'הטלפון מחובר ומתכונן. זה לוקח דקה.',
  'Saving files now…': 'הקבצים נשמרים עכשיו…',
  'Files saved {time}': 'הקבצים נשמרו {time}',
  'Waiting for its first files': 'ממתין לקבצים הראשונים',
  'Computers are backed up automatically, following their backup plan. Phones save files as soon as they are added in the app.':
    'מחשבים מגובים אוטומטית, לפי תוכנית הגיבוי שלהם. טלפונים שומרים קבצים ברגע שהם מתווספים באפליקציה.',
  'Not saved for {time}': 'לא נשמר כבר {time}',
  // ───────── folder archives ─────────
  "An archive keeps a folder in the cloud. Old files then leave this computer to free up space, and stay one click away.": "ארכיון שומר תיקייה בענן. קבצים ישנים יוצאים אחר כך מהמחשב כדי לפנות מקום, ונשארים במרחק לחיצה.",
  "kept for {time} after leaving the computer": "נשמר {time} אחרי שיוצא מהמחשב",
  "Choose the folder to keep in the cloud, for example a folder with one subfolder per event.": "יש לבחור את התיקייה שתישמר בענן, למשל תיקייה עם תת־תיקייה לכל אירוע.",
  "When should files leave this computer?": "מתי קבצים יוצאים מהמחשב?",
  "Everything in the folder is copied to the cloud right away and can be shared. These rules only decide when a file is removed from this computer to free up space. Choose one or both; the oldest files go first.": "כל מה שבתיקייה מועתק לענן מיד, ואפשר לשתף אותו. הכללים האלה קובעים רק מתי קובץ יימחק מהמחשב כדי לפנות מקום. אפשר לבחור אחד או את שניהם; הקבצים הישנים ביותר יוצאים ראשונים.",
  "What leaves the computer together?": "מה יוצא מהמחשב יחד?",
  "Every file is judged by its own date. Part of a folder can leave the computer while the rest stays.": "כל קובץ נבחן לפי התאריך שלו. חלק מתיקייה יכול לצאת מהמחשב בזמן ששאר הקבצים נשארים.",
  "Each folder directly inside (for example, one per event) leaves the computer as a whole, once nothing in it has changed for the chosen time.": "כל תיקייה שנמצאת ישירות בפנים (למשל אחת לכל אירוע) יוצאת מהמחשב בשלמותה, אחרי ששום דבר בה לא השתנה במשך הזמן שנבחר.",
  "A file that is no longer on the computer is kept in the cloud for {time}, then deleted for good. Files still on the computer are always kept in the cloud.": "קובץ שכבר לא נמצא במחשב נשמר בענן {time}, ואז נמחק לצמיתות. קבצים שעדיין נמצאים במחשב תמיד נשמרים בענן.",
  "Over the limit: freeing up space": "מעל המגבלה: מפנה מקום",
  "Leaves the computer": "יוצא מהמחשב",
  "Last copied": "הועתק לאחרונה",
  "{time} after leaving the computer, then deleted for good": "{time} אחרי היציאה מהמחשב, ואז נמחק לצמיתות",
  "Until the ZeroGap app on this computer updates itself, files are copied to the cloud only when they leave the computer.": "עד שהאפליקציה של ZeroGap במחשב הזה תתעדכן מעצמה, קבצים מועתקים לענן רק כשהם יוצאים מהמחשב.",
  "Nothing has been copied yet": "עוד לא הועתק דבר",
  "Copied to the cloud": "הועתק לענן",
  "leaves the computer soon": "ייצא מהמחשב בקרוב",
  "leaves the computer in about a day": "ייצא מהמחשב בעוד יום בערך",
  "leaves the computer in about {n} days": "ייצא מהמחשב בעוד כ־{n} ימים",
  "How long the archive keeps files": "כמה זמן הארכיון שומר קבצים",
  "Delete them for good and save": "למחוק אותם לצמיתות ולשמור",
  "How long should the cloud keep them?": "כמה זמן לשמור אותם בענן?",
  "Counted from the day each file moved to the cloud.": "נספר מהיום שבו כל קובץ עבר לענן.",
  "Forever": "לתמיד",
  "For a set time": "לזמן מוגדר",
  "How long": "כמה זמן",
  "Unit": "יחידה",
  "months": "חודשים",
  "years": "שנים",
  "then deleted for good": "ואז נמחקים לצמיתות",
  "After {time} in the cloud, a file is deleted for good. If it is no longer on the computer, it cannot be brought back.": "אחרי {time} בענן, קובץ נמחק לצמיתות. אם הוא כבר לא במחשב, לא יהיה אפשר להחזיר אותו.",
  "This deletes {files} files ({size}) for good": "השינוי הזה ימחק לצמיתות {files} קבצים ({size})",
  "They have already been in the cloud longer than the new setting allows, so they are deleted within the hour. Files that are no longer on the computer cannot be brought back.": "הם כבר בענן יותר זמן ממה שההגדרה החדשה מאפשרת, ולכן יימחקו בתוך שעה. קבצים שכבר לא במחשב לא יהיה אפשר להחזיר.",
  "Nothing is lost by moving": "ההעברה לענן לא מאבדת כלום",
  "A file is removed from this computer only after its copy in the cloud has been read back and checked. It can be brought back until the cloud deletes it.": "קובץ נמחק מהמחשב רק אחרי שהעותק שלו בענן נקרא בחזרה ונבדק. אפשר להחזיר אותו כל עוד הוא נשמר בענן.",
  "How long it keeps files": "כמה זמן נשמרים הקבצים",
  "Kept in the cloud": "נשמרים בענן",
  "{time}, then deleted for good": "{time}, ואז נמחקים לצמיתות",
  "Deleted for good": "נמחק לצמיתות",
  "{files} files deleted for good {time}": "{files} קבצים נמחקו לצמיתות {time}",
  "deleted from the cloud for good on {date}": "יימחק מהענן לצמיתות ב־{date}",
  "deleted for good after {time}": "נמחקים לצמיתות אחרי {time}",
  "Open folder": "פתיחת התיקייה",
  "Close folder": "סגירת התיקייה",
  "older than {n} days": "שלא השתנו {n} ימים",
  "over {size}": "גדולה מ־{size}",
  "Moves files {age}, or when the folder is {size}": "מעביר קבצים {age}, או כשהתיקייה {size}",
  "Moves files {limit}": "מעביר קבצים {limit}",
  "Archives": "ארכיונים",
  "Add archive": "הוספת ארכיון",
  "An archive moves old files from a folder to the cloud, to free up space on this computer. They stay one click away.": "ארכיון מעביר קבצים ישנים מתיקייה לענן, כדי לפנות מקום במחשב. הם נשארים במרחק לחיצה.",
  "Folder not found": "התיקייה לא נמצאה",
  "No longer watched": "כבר לא במעקב",
  "{size} in the cloud": "{size} בענן",
  "To use archives, the ZeroGap app on this computer needs an update. It updates itself; this usually takes a day.": "כדי להשתמש בארכיונים, האפליקציה של ZeroGap במחשב הזה צריכה עדכון. העדכון מתבצע מעצמו, ובדרך כלל לוקח עד יום.",
  "Archive saved": "הארכיון נשמר",
  "Archive added. The computer checks the folder within a few minutes.": "הארכיון נוסף. המחשב יבדוק את התיקייה בעוד כמה דקות.",
  "Archive settings": "הגדרות ארכיון",
  "Add an archive": "הוספת ארכיון",
  "Which folder?": "איזו תיקייה?",
  "Choose the folder whose old files should move to the cloud, for example a folder with one subfolder per event.": "יש לבחור את התיקייה שהקבצים הישנים שלה יעברו לענן, למשל תיקייה עם תת־תיקייה לכל אירוע.",
  "When should files move to the cloud?": "מתי קבצים עוברים לענן?",
  "Choose one or both. The oldest files move first.": "אפשר לבחור אחת או את שתיהן. הקבצים הישנים ביותר עוברים ראשונים.",
  "When a file has not changed for": "כשקובץ לא השתנה במשך",
  "Days": "ימים",
  "days": "ימים",
  "When the folder is bigger than": "כשהתיקייה גדולה מ־",
  "Gigabytes": "ג׳יגה־בייט",
  "Choose at least one.": "יש לבחור לפחות אחת.",
  "What moves together?": "מה עובר יחד?",
  "Each file on its own": "כל קובץ בנפרד",
  "Whole folders inside it": "תיקיות שלמות שבתוכה",
  "Every file is judged by its own date. A folder can end up partly on the computer and partly in the cloud.": "כל קובץ נבדק לפי התאריך שלו. תיקייה יכולה להישאר חלקה במחשב וחלקה בענן.",
  "Each folder directly inside (for example, one per event) moves as a whole, once nothing in it has changed for the chosen time.": "כל תיקייה שנמצאת ישירות בתוכה (למשל אחת לכל אירוע) עוברת בשלמותה, אחרי ששום דבר בה לא השתנה במשך הזמן שנבחר.",
  "Nothing is lost": "שום דבר לא הולך לאיבוד",
  "A file is removed from this computer only after its copy in the cloud has been read back and checked. It can be brought back at any time.": "קובץ נמחק מהמחשב רק אחרי שהעותק שלו בענן נקרא בחזרה ונבדק. תמיד אפשר להחזיר אותו.",
  "This is not a folder on the computer": "זו לא תיקייה במחשב",
  "Folders on another computer or a network drive cannot be archived": "אי אפשר להעביר לארכיון תיקיות ממחשב אחר או מכונן רשת",
  "A whole drive cannot be archived; choose a folder inside it": "אי אפשר להעביר לארכיון כונן שלם; יש לבחור תיקייה בתוכו",
  "Windows and program folders cannot be archived": "אי אפשר להעביר לארכיון את תיקיות Windows והתוכנות",
  "A person's whole user folder cannot be archived; choose a folder inside it, such as Desktop or Pictures": "אי אפשר להעביר לארכיון את כל התיקייה של משתמש; יש לבחור תיקייה בתוכה, כמו שולחן העבודה או תמונות",
  "ZeroGap's own folder cannot be archived": "אי אפשר להעביר לארכיון את התיקייה של ZeroGap עצמה",
  "This folder is inside another archive on this computer": "התיקייה הזו נמצאת בתוך ארכיון אחר במחשב הזה",
  "This folder contains another archive on this computer": "התיקייה הזו מכילה ארכיון אחר במחשב הזה",
  "The computer did not answer.": "המחשב לא ענה.",
  "Opening…": "פותח…",
  "No folders inside": "אין תיקיות בפנים",
  "Only the first 2,000 folders are shown": "מוצגות רק 2,000 התיקיות הראשונות",
  "Connecting to the computer…": "מתחבר למחשב…",
  "The computer is not answering": "המחשב לא עונה",
  "The folders could not be listed": "לא ניתן היה להציג את התיקיות",
  "To choose a folder, the computer must be on and connected to the internet.": "כדי לבחור תיקייה, המחשב צריך להיות דלוק ומחובר לאינטרנט.",
  "Usual places": "מקומות נפוצים",
  "Drives": "כוננים",
  "Chosen:": "נבחרה:",
  "Open the folders and click the one to archive.": "יש לפתוח את התיקיות וללחוץ על זו שתעבור לארכיון.",
  "Sending to the cloud": "נשלח לענן",
  "Checking the cloud copy": "העותק בענן נבדק",
  "Freeing up space": "מפנה מקום",
  "Didn't finish, nothing was removed": "לא הסתיים, שום דבר לא נמחק",
  "Archive": "ארכיון",
  "Stop archiving": "הפסקת הארכיון",
  "On this computer": "במחשב הזה",
  "This archive was stopped {time}. The folder is no longer watched; what is in the cloud stays there and can be brought back.": "הארכיון הופסק {time}. התיקייה כבר לא במעקב; מה שבענן נשאר שם ואפשר להחזיר אותו.",
  "Folder not found on the computer": "התיקייה לא נמצאה במחשב",
  "Over the limit: moving the oldest files": "מעל המגבלה: הקבצים הישנים ביותר עוברים לענן",
  "Watching the folder": "התיקייה במעקב",
  "Rule": "כלל",
  "Moves": "מה עובר",
  "Still on the computer": "עדיין במחשב",
  "Last checked": "נבדק לאחרונה",
  "Last time something went wrong": "בפעם האחרונה משהו השתבש",
  "No file was removed. The computer tries again on its next check.": "שום קובץ לא נמחק. המחשב ינסה שוב בבדיקה הבאה.",
  "In the cloud": "בענן",
  "Space freed on the computer": "מקום שהתפנה במחשב",
  "Last moved": "הועבר לאחרונה",
  "Every file was read back from the cloud and checked before it left the computer.": "כל קובץ נקרא בחזרה מהענן ונבדק לפני שנמחק מהמחשב.",
  "Kept on the computer ({n})": "נשמרים במחשב ({n})",
  "Kept on the computer": "נשמרים במחשב",
  "Get the chosen files back": "שחזור הקבצים שנבחרו",
  "Nothing has moved yet": "עדיין לא הועבר כלום",
  "Moved": "הועבר",
  "Removed from the computer": "נמחק מהמחשב",
  "Files brought back to where they were stay on the computer for the time chosen, even if they are older than the rule.": "קבצים שהוחזרו למקומם נשארים במחשב למשך הזמן שנבחר, גם אם הם ישנים יותר מהכלל.",
  "Nothing is being kept": "אין קבצים שנשמרים כרגע",
  "Until {time}": "עד {time}",
  "Until released": "עד לשחרור",
  "Let it move again": "לאפשר העברה שוב",
  "Stop archiving this folder?": "להפסיק את הארכיון של התיקייה הזו?",
  "Archiving stopped": "הארכיון הופסק",
  "The computer stops moving files from this folder. Nothing on the computer changes.": "המחשב יפסיק להעביר קבצים מהתיקייה הזו. שום דבר במחשב לא משתנה.",
  "Files already in the cloud stay there, and you can still bring them back from this page.": "קבצים שכבר בענן נשארים שם, ועדיין אפשר להחזיר אותם מהדף הזה.",
  "On the computer": "במחשב",
  "On the computer and in the cloud": "במחשב ובענן",
  "moves to the cloud soon": "יעבור לענן בקרוב",
  "moves to the cloud in about a day": "יעבור לענן בעוד יום בערך",
  "moves to the cloud in about {n} days": "יעבור לענן בעוד כ־{n} ימים",
  "Search this archive": "חיפוש בארכיון",
  "Where": "איפה",
  "This folder is empty": "התיקייה ריקה",
  "Moving old files to the archive…": "קבצים ישנים עוברים לארכיון…",
  "Moving old files to the archive": "העברת קבצים ישנים לארכיון",
  "From where": "מאיפה",
  "They stay on the computer until you let them move again.": "הם יישארו במחשב עד שיאופשר להעביר אותם שוב.",
  "They stay on the computer for {n} days before they can move to the cloud again.": "הם יישארו במחשב {n} ימים לפני שיוכלו לעבור שוב לענן.",
  "Get {what} from the archive {folder} of {name}, and {where}.": "לשחזר {what} מהארכיון {folder} של {name}, ו{where}.",
  "Not backed up yet": "עדיין לא גובה",
  "Where are the files?": "איפה הקבצים?",
  "In a saved backup": "בגיבוי שמור",
  "Files as they were on the computer at a backup: also files that were deleted or changed since.": "קבצים כפי שהיו במחשב בזמן גיבוי, כולל קבצים שנמחקו או השתנו מאז.",
  "In an archive": "בארכיון",
  "Old files that moved to the cloud to free up space on the computer.": "קבצים ישנים שעברו לענן כדי לפנות מקום במחשב.",
  "Back in the archive folder, as before. If a file with the same name is there, both are kept.": "בחזרה לתיקיית הארכיון, כמו קודם. אם יש שם קובץ באותו שם, שניהם נשמרים.",
  "Keep them on the computer for": "להשאיר אותם במחשב למשך",
  "Old files would otherwise move to the cloud again at the next check.": "אחרת קבצים ישנים יעברו שוב לענן בבדיקה הבאה.",
  "Until I let them move": "עד שאאפשר להעביר אותם",
  "Archive: {folder}": "ארכיון: {folder}",
  "From the archive {folder} · requested {requested}": "מהארכיון {folder} · התבקש {requested}",
  "Shared links": "קישורי שיתוף",
  "No shared links": "אין קישורי שיתוף",
  "Made by": "נוצר על ידי",
  "Opened": "נפתח",
  "Stop link": "עצירת הקישור",
  "Stop shared link: {name}": "עצירת קישור שיתוף: {name}",
  "For abuse reports. The link stops at once, the organization sees that ZeroGap support stopped it, and the files are not touched.": "לטיפול בדיווחי שימוש לרעה. הקישור מפסיק לעבוד מיד, הארגון רואה שהתמיכה של ZeroGap עצרה אותו, והקבצים עצמם לא נפגעים.",
  "Share": "שיתוף",
  "Share everything in this folder": "שיתוף כל מה שבתיקייה הזו",
  "7 days": "7 ימים",
  "30 days": "30 ימים",
  "Pick a date": "בחירת תאריך",
  "No end date": "ללא תאריך סיום",
  "Photo gallery": "גלריית תמונות",
  "Big pictures in a grid. Best for photos.": "תמונות גדולות ברשת. הכי מתאים לתמונות.",
  "List of files": "רשימת קבצים",
  "Names, sizes and folders. Best for documents.": "שמות, גדלים ותיקיות. הכי מתאים למסמכים.",
  "How it looks": "איך זה נראה",
  "Send the password separately from the link. For example, the link by email and the password in a text message.": "כדאי לשלוח את הסיסמה בנפרד מהקישור. למשל, את הקישור במייל ואת הסיסמה בהודעת טקסט.",
  "Use at least {n} characters.": "נדרשים לפחות {n} תווים.",
  "Hide": "הסתרה",
  "Make one for me": "יצירת סיסמה בשבילי",
  "Your link is ready": "הקישור מוכן",
  "Open the link's page": "לדף הקישור",
  "Anyone who has this link and its password can see and download these files.": "עם הקישור והסיסמה אפשר לראות ולהוריד את הקבצים האלה, בלי חשבון.",
  "Anyone who has this link can see and download these files.": "עם הקישור אפשר לראות ולהוריד את הקבצים האלה, בלי חשבון.",
  "It works until {date}.": "הוא פעיל עד {date}.",
  "It works until you stop it.": "הוא פעיל עד שעוצרים אותו.",
  "Link": "קישור",
  "Send by email": "שליחה במייל",
  "Send on WhatsApp": "שליחה בוואטסאפ",
  "Send the password separately from the link. It is not shown again, but you can set a new one on the link's page.": "כדאי לשלוח את הסיסמה בנפרד מהקישור. היא לא תוצג שוב, אבל אפשר לקבוע סיסמה חדשה בדף הקישור.",
  "Share files": "שיתוף קבצים",
  "Create link": "יצירת קישור",
  "Shared links are turned off for this organization. An admin can turn them on in Settings.": "קישורי שיתוף כבויים בארגון הזה. מנהל יכול להפעיל אותם בהגדרות.",
  "{n} pictures": "{n} תמונות",
  "A shared link can hold up to {size} and {files} files. Choose fewer.": "קישור שיתוף יכול להכיל עד {size} ו-{files} קבצים. יש לבחור פחות.",
  "There are no files to share in this selection.": "אין בבחירה הזו קבצים לשיתוף.",
  "The person who opens the link sees this as the title.": "זו הכותרת שמוצגת בפתיחת הקישור.",
  "For example: Wedding photos": "למשל: תמונות מהחתונה",
  "The link works for": "משך הזמן שהקישור פעיל",
  "Some of these files are kept in the cloud only until {date}. The link stops showing them then.": "חלק מהקבצים האלה נשמרים בענן רק עד {date}. מאז הקישור כבר לא יציג אותם.",
  "Ending the link does not delete any files. If your backup plan removes this backup, the link stops working.": "סיום הקישור לא מוחק אף קובץ. אם תוכנית הגיבוי מוחקת את הגיבוי הזה, הקישור מפסיק לעבוד.",
  "Protect with a password": "הגנה בסיסמה",
  "Your organization asks for a password on every shared link.": "הארגון שלך דורש סיסמה לכל קישור שיתוף.",
  "Choose the files and folders to share": "בחירת הקבצים והתיקיות לשיתוף",
  "Its end date passed": "תאריך הסיום שלו עבר",
  "Its files were removed from the backup": "הקבצים שלו הוסרו מהגיבוי",
  "The person who made it left the organization": "יוצר הקישור כבר לא בארגון",
  "Its computer was removed": "המחשב שלו הוסר",
  "Shared links were turned off": "קישורי השיתוף כובו",
  "Stopped by ZeroGap support": "נעצר על ידי התמיכה של ZeroGap",
  "Ended": "הסתיים",
  "Ends {time}": "מסתיים {time}",
  "Archive of {folder}": "ארכיון של {folder}",
  "Backup of {date}": "גיבוי מ-{date}",
  "Links that let people outside your organization see and download files from your backups. Every time someone opens one or downloads a file, it is recorded.": "קישורים שמאפשרים לאנשים מחוץ לארגון לראות ולהוריד קבצים מהגיבויים. כל פתיחה של קישור וכל הורדה של קובץ נרשמות.",
  "Working now": "פעילים עכשיו",
  "No links are working right now": "אין כרגע קישורים פעילים",
  "No shared links yet": "עדיין אין קישורי שיתוף",
  "To share files, open a computer’s backups or an archive, choose the files, and press Share.": "כדי לשתף קבצים, פותחים את הגיבויים של מחשב או ארכיון, בוחרים את הקבצים ולוחצים על שיתוף.",
  "password": "סיסמה",
  "Someone who left": "משתמש שעזב",
  "until {date}": "עד {date}",
  "Unknown browser": "דפדפן לא מוכר",
  "{browser} on {os}": "{browser} ב-{os}",
  "Someone": "משתמש",
  "{who} made the link": "יצירת הקישור · {who}",
  "{who} changed the link": "שינוי הקישור · {who}",
  "{who} copied the link": "העתקת הקישור · {who}",
  "{who} stopped the link": "עצירת הקישור · {who}",
  "ZeroGap support stopped the link": "התמיכה של ZeroGap עצרה את הקישור",
  "The link ended": "הקישור הסתיים",
  "Opened the link": "פתיחת הקישור",
  "Typed a wrong password": "הקלדת סיסמה שגויה",
  "Locked out for 15 minutes after too many wrong passwords": "חסימה ל-15 דקות אחרי יותר מדי סיסמאות שגויות",
  "Tried the link after it stopped working": "ניסיון לפתוח את הקישור אחרי שהפסיק לעבוד",
  "Downloaded the folder": "הורדת התיקייה",
  "Downloaded everything ({n} files)": "הורדת הכול ({n} קבצים)",
  "Keep the password": "השארת הסיסמה",
  "No password": "בלי סיסמה",
  "Add a password": "הוספת סיסמה",
  "Remove the password": "הסרת הסיסמה",
  "Change the link": "שינוי הקישור",
  "The link works": "הקישור פעיל",
  "Now: until {date}.": "כרגע: עד {date}.",
  "Now: until you stop it.": "כרגע: עד שעוצרים אותו.",
  "As it is": "בלי שינוי",
  "People who already opened the link will need the new password.": "גם בפתיחה חוזרת של הקישור תידרש הסיסמה החדשה.",
  "Shared files": "הקבצים ששותפו",
  "Nothing here": "אין כאן כלום",
  "Link copied": "הקישור הועתק",
  "Copy link": "העתקת הקישור",
  "Change": "שינוי",
  "Stop sharing": "הפסקת השיתוף",
  "This link no longer works.": "הקישור הזה כבר לא עובד.",
  "It was stopped {time}.": "הוא נעצר {time}.",
  "The files themselves were not touched.": "הקבצים עצמם לא נפגעו.",
  "Send the password separately from the link. It is not shown again.": "כדאי לשלוח את הסיסמה בנפרד מהקישור. היא לא תוצג שוב.",
  "About this link": "על הקישור",
  "Shared from": "שותף מתוך",
  "Works until": "פעיל עד",
  "You stop it": "עד שעוצרים אותו",
  "Yes": "כן",
  "No": "לא",
  "Who opened it": "מי פתח אותו",
  "times opened": "פתיחות",
  "downloads": "הורדות",
  "Nobody has opened it yet.": "הקישור עוד לא נפתח.",
  "Address": "כתובת",
  "Changes": "שינויים",
  "with the assistant": "בעזרת העוזר",
  "Stop sharing?": "להפסיק את השיתוף?",
  "The link stops working right away, also for anyone who has it open now. The files themselves are not touched. This cannot be undone, but you can make a new link.": "הקישור יפסיק לעבוד מיד, גם למי שפתח אותו עכשיו. הקבצים עצמם לא ייפגעו. אי אפשר לבטל את זה, אבל אפשר ליצור קישור חדש.",
  "Allow shared links": "לאפשר קישורי שיתוף",
  "Admins can make links that let people outside the organization see and download chosen files.": "מנהלים יכולים ליצור קישורים שמאפשרים לאנשים מחוץ לארגון לראות ולהוריד קבצים שנבחרו.",
  "Always require a password": "לדרוש תמיד סיסמה",
  "Turn off shared links?": "לכבות את קישורי השיתוף?",
  "Turn off": "כיבוי",
  "{n} links that work now will stop for good. Turning sharing back on does not bring them back.": "{n} קישורים שפעילים עכשיו יפסיקו לעבוד לצמיתות. הפעלה מחדש של השיתוף לא תחזיר אותם.",
  "Every link that works now will stop for good. Turning sharing back on does not bring them back.": "כל קישור שפעיל עכשיו יפסיק לעבוד לצמיתות. הפעלה מחדש של השיתוף לא תחזיר אותם.",
  "made a shared link": "יצירת קישור שיתוף",
  "changed a shared link": "שינוי קישור שיתוף",
  "copied a shared link": "העתקת קישור שיתוף",
  "stopped a shared link": "עצירת קישור שיתוף",
  "a shared link ended": "קישור שיתוף הסתיים",
  "opened a shared link": "פתיחת קישור שיתוף",
  "typed a wrong password for a shared link": "הקלדת סיסמה שגויה לקישור שיתוף",
  "a shared link was locked after too many wrong passwords": "נעילת קישור שיתוף אחרי יותר מדי סיסמאות שגויות",
  "tried a shared link that no longer works": "ניסיון לפתוח קישור שיתוף שכבר לא עובד",
  "downloaded a file from a shared link": "הורדת קובץ מקישור שיתוף",
  "downloaded files from a shared link": "הורדת קבצים מקישור שיתוף",
  "Someone with the link": "מי שקיבל את הקישור",
  "{n} bytes": "{n} בתים",
  "Open {name}": "פתיחת {name}",
  "Some pictures are still being prepared. Refresh the page in a minute to see them.": "חלק מהתמונות עדיין בהכנה. אפשר לרענן את הדף בעוד דקה כדי לראות אותן.",
  "Other files": "קבצים נוספים",
  "Download {name}": "הורדת {name}",
  "Download the folder {name}": "הורדת התיקייה {name}",
  "Download folder": "הורדת התיקייה",
  "All files": "כל הקבצים",
  "This folder is empty.": "התיקייה הזו ריקה.",
  "Shared safely with ZeroGap": "שותף בבטחה עם ZeroGap",
  "Report a problem": "דיווח על בעיה",
  "This link isn't available": "הקישור הזה לא זמין",
  "It may have ended or been stopped. Ask the person who sent it for a new one.": "ייתכן שהוא הסתיים או נעצר. אפשר לבקש קישור חדש ממי ששלח אותו.",
  "Too many tries. Try again in {n} minutes.": "יותר מדי ניסיונות. אפשר לנסות שוב בעוד {n} דקות.",
  "That password didn't work. Check it and try again.": "הסיסמה לא התאימה. כדאי לבדוק אותה ולנסות שוב.",
  "This link is protected": "הקישור הזה מוגן",
  "Type the password you were given.": "יש להקליד את הסיסמה שהתקבלה יחד עם הקישור.",
  "Shared by {org}": "שותף על ידי {org}",
  "Available until {date}": "זמין עד {date}",
  "Download all": "הורדת הכול",
  "Too many requests. Try again in a little while.": "יותר מדי בקשות. אפשר לנסות שוב בעוד כמה רגעים.",
  "Make a link to one file ({size}) from {computer}, called “{name}”.": "יצירת קישור לקובץ אחד ({size}) מ-{computer}, בשם „{name}”.",
  "Make a link to {files} files ({size}) from {computer}, called “{name}”.": "יצירת קישור ל-{files} קבצים ({size}) מ-{computer}, בשם „{name}”.",
  "Anyone who has the link and its password can see and download these files until {date}.": "עם הקישור והסיסמה אפשר יהיה לראות ולהוריד את הקבצים האלה עד {date}.",
  "Anyone who has the link can see and download these files until {date}. No password.": "עם הקישור אפשר יהיה לראות ולהוריד את הקבצים האלה עד {date}. בלי סיסמה.",
  "It shows the files as a photo gallery.": "הקבצים יוצגו כגלריית תמונות.",
  "It shows the files as a list.": "הקבצים יוצגו כרשימה.",
  "Every visit and download is recorded, and you can stop the link at any time.": "כל כניסה וכל הורדה נרשמות, ואפשר לעצור את הקישור בכל רגע.",
  "Change the shared link “{name}”:": "שינוי קישור השיתוף „{name}”:",
  "Show it as a photo gallery": "הצגה כגלריית תמונות",
  "Show it as a list": "הצגה כרשימה",
  "Make it end on {date}": "סיום ב-{date}",
  "Stop the shared link “{name}” now. It stops working at once, also for anyone who has it open.": "עצירת קישור השיתוף „{name}” עכשיו. הוא יפסיק לעבוד מיד, גם למי שפתח אותו כרגע.",
  "The files themselves are not touched.": "הקבצים עצמם לא ייפגעו.",
  "Create the link": "יצירת הקישור",
  "Open|verb": "פתיחה",
  "Downloaded|a file": "הורדת הקובץ",
  "Files shared with you": "קבצים ששותפו איתך",
  'Accessibility': 'נגישות',
  'Accessibility statement': 'הצהרת נגישות',
  '{n} open': '{n} פתוחות',
};
